import { supabase } from "../lib/supabase";
import { isAdminProfile, resolveCurrentSessionAdminState } from "../utils/adminAccess";
import { MONTHLY_CHALLENGE_KEY } from "../data/monthlyChallenge";
import { compressImageForUpload, createImageThumbnail } from "../utils/imageCompression";
import { KOFI_CONFIG } from "../config/kofi";
import { consumeAIScanQuota } from "./aiScanService";

const BUCKET_NAME = "texture-uploads";
const TABLE_NAME = "texture_showcase_submissions";
const REACTION_TABLE_NAME = "texture_showcase_reactions";
const REACTION_COUNT_VIEW_NAME = "texture_showcase_reaction_counts";
const REACTION_TYPE_BONGO_HEART = "bongo_heart";
const REACTION_TYPE_GORGEOUS = "gorgeous";
const REACTION_TYPE_INSPIRING = "inspiring";
const WALL_REACTION_TYPES = [
  REACTION_TYPE_BONGO_HEART,
  REACTION_TYPE_GORGEOUS,
  REACTION_TYPE_INSPIRING,
];
export const WALL_REACTION_TYPE = {
  CUTE: REACTION_TYPE_BONGO_HEART,
  GORGEOUS: REACTION_TYPE_GORGEOUS,
  INSPIRING: REACTION_TYPE_INSPIRING,
};
const ADMIN_REACTION_WEIGHT = 3;
const SIGNED_URL_TTL_SECONDS = 60 * 60;
// Client-side signed URL cache — avoids re-generating the same URL on every render.
// TTL is 50 min (10 min safety buffer before the 60-min Supabase expiry).
const SIGNED_URL_CACHE_TTL_MS = 50 * 60 * 1000;
const signedUrlCache = new Map(); // key → { url: string, expiresAt: number }
const WALL_VISIBILITY_WINDOW_DAYS = 30;
const WALL_SUBMISSION_SCAN_COST = KOFI_CONFIG.wallSubmissionScanCost;
// Keep aligned with AI scan upload limits so a successful scan can still be shared.
const MAX_TEXTURE_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif"
]);
const WALL_STATUSES = new Set(["pending", "approved", "removed", "expired"]);
const MONTHLY_FEATURE_STATUSES = new Set(["none", "candidate", "selected"]);
const MONTHLY_CHALLENGE_STATUSES = new Set(["none", "participant", "candidate", "selected"]);
const SELECTION_TYPES = new Set(["general", "contributor", "challenge"]);
const SKILL_LEVELS = new Set(["beginner", "intermediate", "expert"]);
const TIKTOK_USERNAME_REGEX = /^[A-Za-z0-9._]{2,24}$/;
const MAX_ARTWORK_TITLE_LENGTH = 80;
const PROTECTED_WALL_EXPIRY_DATE = "2999-12-31T23:59:59.000Z";
const SKILL_LEVEL_ALIASES = {
  advanced: "expert"
};

const SELECT_COLUMN_LIST = [
  "id",
  "user_id",
  "showcase_name",
  "artwork_title",
  "submitted_by_contributor",
  "showcase_consent",
  "showcase_status",
  "wall_status",
  "wall_approved_at",
  "wall_expires_at",
  "removed_from_wall_at",
  "removed_from_wall_by",
  "monthly_feature_consent",
  "monthly_feature_status",
  "monthly_feature_category",
  "monthly_challenge_key",
  "participates_in_monthly_challenge",
  "monthly_challenge_status",
  "monthly_challenge_category",
  "monthly_challenge_selected_at",
  "monthly_challenge_xp_bonus_awarded",
  "monthly_challenge_xp_bonus_awarded_at",
  "monthly_challenge_level_up_awarded",
  "monthly_challenge_level_up_awarded_at",
  "selection_type",
  "selected_month",
  "selected_at",
  "challenge_theme",
  "challenge_name",
  "monthly_challenge_title",
  "spotlight_note",
  "spotlight_tiktok_username",
  "spotlight_tiktok_updated_at",
  "declared_skill_level",
  "scan_type",
  "texture_type",
  "image_url",
  "thumbnail_storage_path",
  "ai_score",
  "created_at"
];
const PREVIEW_SELECT_COLUMN_LIST = [
  "id",
  "user_id",
  "showcase_name",
  "artwork_title",
  "submitted_by_contributor",
  "showcase_consent",
  "showcase_status",
  "wall_status",
  "wall_approved_at",
  "wall_expires_at",
  "monthly_challenge_key",
  "participates_in_monthly_challenge",
  "selection_type",
  "selected_month",
  "selected_at",
  "challenge_theme",
  "challenge_name",
  "monthly_challenge_title",
  "spotlight_tiktok_username",
  "spotlight_tiktok_updated_at",
  "scan_type",
  "image_url",
  "thumbnail_storage_path",
  "created_at"
];
const PUBLIC_WALL_SELECT_COLUMN_LIST = [
  "id",
  "user_id",
  "showcase_name",
  "artwork_title",
  "submitted_by_contributor",
  "showcase_consent",
  "showcase_status",
  "wall_status",
  "wall_approved_at",
  "wall_expires_at",
  "removed_from_wall_at",
  "monthly_challenge_key",
  "participates_in_monthly_challenge",
  "monthly_challenge_status",
  "declared_skill_level",
  "scan_type",
  "texture_type",
  "image_url",
  "thumbnail_storage_path",
  "ai_score",
  "created_at"
];
const PUBLIC_WALL_PREVIEW_SELECT_COLUMN_LIST = [
  "id",
  "user_id",
  "showcase_name",
  "artwork_title",
  "submitted_by_contributor",
  "showcase_consent",
  "showcase_status",
  "wall_status",
  "wall_approved_at",
  "wall_expires_at",
  "removed_from_wall_at",
  "monthly_challenge_key",
  "participates_in_monthly_challenge",
  "monthly_challenge_status",
  "scan_type",
  "image_url",
  "thumbnail_storage_path",
  "created_at"
];
const SPOTLIGHT_SELECT_COLUMN_LIST = [
  "id",
  "user_id",
  "showcase_name",
  "artwork_title",
  "image_url",
  "thumbnail_storage_path",
  "submitted_by_contributor",
  "selection_type",
  "selected_month",
  "selected_at",
  "challenge_theme",
  "spotlight_note",
  "spotlight_tiktok_username",
  "spotlight_tiktok_updated_at",
  "showcase_consent",
  "wall_status",
  "showcase_status",
  "removed_from_wall_at",
  "created_at"
];
const SPOTLIGHT_DIAGNOSTIC_SELECT_COLUMN_LIST = [
  "id",
  "user_id",
  "showcase_name",
  "artwork_title",
  "selection_type",
  "selected_month",
  "selected_at",
  "challenge_theme",
  "spotlight_note",
  "spotlight_tiktok_username",
  "wall_status",
  "showcase_status",
  "showcase_consent",
  "image_url",
  "thumbnail_storage_path",
  "removed_from_wall_at",
  "submitted_by_contributor",
  "participates_in_monthly_challenge",
  "monthly_challenge_status",
  "monthly_feature_status",
  "monthly_challenge_key",
  "created_at"
];
const SPOTLIGHT_REQUIRED_COLUMNS = new Set([
  "id",
  "user_id",
  "selection_type",
  "selected_month",
  "selected_at",
  "image_url",
  "showcase_consent",
]);
const SCHEMA_CACHE_MISSING_COLUMN_CODE = "PGRST204";
const ignoredSchemaColumns = new Set();
const ignoredSpotlightSchemaColumns = new Set();
const ignoredSpotlightDiagnosticSchemaColumns = new Set();

// Columns guaranteed to exist in the original table (pre-migration baseline).
// Used as a last-resort nuclear fallback if the schema cache is stale for many columns.
const CORE_INSERT_COLUMNS = new Set([
  "user_id",
  "showcase_name",
  "showcase_consent",
  "showcase_status",
  "monthly_feature_consent",
  "monthly_feature_status",
  "monthly_feature_category",
  "texture_type",
  "image_url",
  "ai_score",
  "created_at"
]);

const CORE_SELECT_COLUMNS = [
  "id",
  "user_id",
  "showcase_name",
  "showcase_consent",
  "showcase_status",
  "monthly_feature_consent",
  "monthly_feature_status",
  "monthly_feature_category",
  "texture_type",
  "image_url",
  "ai_score",
  "created_at"
];

function getSelectColumns() {
  return SELECT_COLUMN_LIST
    .filter((columnName) => !ignoredSchemaColumns.has(columnName))
    .join(",\n  ");
}

function getPreviewSelectColumns() {
  return PREVIEW_SELECT_COLUMN_LIST
    .filter((columnName) => !ignoredSchemaColumns.has(columnName))
    .join(",\n  ");
}

function getPublicWallSelectColumns() {
  return PUBLIC_WALL_SELECT_COLUMN_LIST
    .filter((columnName) => !ignoredSchemaColumns.has(columnName))
    .join(",\n  ");
}

function getPublicWallPreviewSelectColumns() {
  return PUBLIC_WALL_PREVIEW_SELECT_COLUMN_LIST
    .filter((columnName) => !ignoredSchemaColumns.has(columnName))
    .join(",\n  ");
}

function getSpotlightSelectColumns() {
  return SPOTLIGHT_SELECT_COLUMN_LIST
    .filter((columnName) => !ignoredSpotlightSchemaColumns.has(columnName))
    .join(",\n  ");
}

function getSpotlightDiagnosticSelectColumns() {
  return SPOTLIGHT_DIAGNOSTIC_SELECT_COLUMN_LIST
    .filter((columnName) => !ignoredSpotlightDiagnosticSchemaColumns.has(columnName))
    .join(",\n  ");
}

function getErrorDebugText(error) {
  return [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(" | ");
}

function extractMissingSchemaColumn(error) {
  const debugText = getErrorDebugText(error);
  const match = debugText.match(/could not find the '([^']+)' column/i);
  return match?.[1] || null;
}

function normalizeMissingColumnName(value) {
  return String(value || "")
    .replace(/"/g, "")
    .split(".")
    .pop()
    .trim();
}

function extractMissingColumnName(error) {
  const fromSchemaCache = extractMissingSchemaColumn(error);
  if (fromSchemaCache) return normalizeMissingColumnName(fromSchemaCache);

  const debugText = getErrorDebugText(error);
  const pgMatch = debugText.match(/column\s+("?[\w.]+"?)\s+does not exist/i);
  if (pgMatch?.[1]) return normalizeMissingColumnName(pgMatch[1]);

  const postgrestMatch = debugText.match(/Could not find a relationship between .* and '([^']+)'/i);
  if (postgrestMatch?.[1]) return normalizeMissingColumnName(postgrestMatch[1]);

  return null;
}

function isMissingColumnError(error) {
  const debugText = getErrorDebugText(error).toLowerCase();
  const code = String(error?.code || "").toUpperCase();
  return (
    code === SCHEMA_CACHE_MISSING_COLUMN_CODE ||
    code === "42703" ||
    debugText.includes("schema cache") ||
    debugText.includes("could not find the") ||
    (debugText.includes("column") && debugText.includes("does not exist"))
  );
}

function registerMissingColumnByName(columnName, contextLabel) {
  const normalizedName = normalizeMissingColumnName(columnName);
  if (!normalizedName) return false;
  const isNewColumn = !ignoredSchemaColumns.has(normalizedName);
  ignoredSchemaColumns.add(normalizedName);

  if (isNewColumn) {
    // eslint-disable-next-line no-console
    console.warn("[SHOWCASE_SCHEMA] missing column registered", {
      context: contextLabel,
      missingColumn: normalizedName,
    });
  }

  return true;
}

function registerMissingSchemaColumn(error, contextLabel) {
  const missingColumn = extractMissingSchemaColumn(error);
  if (!missingColumn) return false;
  const debugText = getErrorDebugText(error).toLowerCase();
  const isSchemaCacheError =
    String(error?.code || "").toUpperCase() === SCHEMA_CACHE_MISSING_COLUMN_CODE ||
    debugText.includes("schema cache");

  if (!isSchemaCacheError) {
    return false;
  }

  // IMPORTANT: do NOT bail out when the column is already in ignoredSchemaColumns.
  // Two concurrent calls (e.g. Promise.all in WallPage) can both receive PGRST204 for the
  // same missing column: the first call adds it and retries successfully, but the second call
  // must also be allowed to retry — at that point getSelectColumns() already excludes the
  // column, so the retry will succeed. Returning false here would make the second call throw
  // and cause "Le Mur est temporairement indisponible" even though the data is accessible.
  const isNewColumn = !ignoredSchemaColumns.has(missingColumn);
  ignoredSchemaColumns.add(missingColumn);

  if (isNewColumn) {
    // eslint-disable-next-line no-console
    console.warn("[SHOWCASE_SCHEMA] missing column fallback applied", {
      context: contextLabel,
      missingColumn,
      code: error?.code || null,
      message: error?.message || null,
    });
  }

  return true;
}

function registerMissingSpotlightSchemaColumn(error, contextLabel) {
  if (!isMissingColumnError(error)) return false;
  const missingColumn = extractMissingColumnName(error);
  if (!missingColumn) return false;

  const normalizedName = normalizeMissingColumnName(missingColumn);
  if (!normalizedName) return false;

  const isNewColumn = !ignoredSpotlightSchemaColumns.has(normalizedName);
  ignoredSpotlightSchemaColumns.add(normalizedName);

  if (isNewColumn) {
    // eslint-disable-next-line no-console
    console.warn("[SPOTLIGHT_SCHEMA] missing column fallback applied", {
      context: contextLabel,
      missingColumn: normalizedName,
      code: error?.code || null,
      message: error?.message || null,
    });
  }

  return true;
}

function registerMissingSpotlightDiagnosticSchemaColumn(error, contextLabel) {
  if (!isMissingColumnError(error)) return false;
  const missingColumn = extractMissingColumnName(error);
  if (!missingColumn) return false;

  const normalizedName = normalizeMissingColumnName(missingColumn);
  if (!normalizedName) return false;

  const isNewColumn = !ignoredSpotlightDiagnosticSchemaColumns.has(normalizedName);
  ignoredSpotlightDiagnosticSchemaColumns.add(normalizedName);

  if (isNewColumn) {
    // eslint-disable-next-line no-console
    console.warn("[SPOTLIGHT_DIAGNOSTIC_SCHEMA] missing column fallback applied", {
      context: contextLabel,
      missingColumn: normalizedName,
      code: error?.code || null,
      message: error?.message || null,
    });
  }

  return true;
}

function stripIgnoredSchemaColumns(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([key]) => !ignoredSchemaColumns.has(key))
  );
}

function buildCoreInsertPayload(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([key]) => CORE_INSERT_COLUMNS.has(key))
  );
}

// maxAttempts is intentionally high (24) to handle any number of missing schema columns
// discovered one at a time by PostgREST. nuclearFactory is a last-resort fallback that
// uses only CORE_INSERT_COLUMNS, guaranteed to exist in any schema version.
async function runWithSchemaFallback(requestFactory, contextLabel, maxAttempts = 24, nuclearFactory = null) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const result = await requestFactory();
    if (!result?.error) return result;
    let shouldRetry = registerMissingSchemaColumn(result.error, contextLabel);
    if (!shouldRetry && isMissingColumnError(result.error)) {
      const missingColumn = extractMissingColumnName(result.error);
      if (missingColumn) {
        shouldRetry = registerMissingColumnByName(missingColumn, contextLabel);
      }
    }
    if (!shouldRetry) return result;
  }

  if (nuclearFactory) {
    // eslint-disable-next-line no-console
    console.warn("[SHOWCASE_SCHEMA] nuclear fallback triggered — using core columns only", { context: contextLabel });
    return nuclearFactory();
  }

  return requestFactory();
}

// CODEX_PROMPT_80 (Marc) : code sentinelle retourne par formatTextureShowcaseError
// quand la limite quotidienne Mur est atteinte. L'appelant
// (ScanPage.handleSubmitShowcase) verifie cette valeur exacte pour afficher
// LimiteMurAtteinteMessage au lieu du message d'erreur rouge generique.
export const DAILY_WALL_LIMIT_SENTINEL = "DAILY_WALL_LIMIT_REACHED";

export function formatTextureShowcaseError(error, language = "fr") {
  const raw = String(error?.message || "").trim();
  const normalized = raw.toLowerCase();

  // CODEX_PROMPT_80 : limite quotidienne Mur atteinte pour un non-contributeur.
  // Le trigger SQL colobongo_check_daily_wall_limit raise 'daily_wall_limit_reached'
  // avec errcode 53300 (resource_exhausted -> HTTP 429 cote PostgREST).
  // On retourne un code sentinelle, pas un message - l'appelant decide quoi afficher.
  if (
    error?.code === "DAILY_WALL_LIMIT_REACHED" ||
    error?.status === 429 ||
    normalized.includes("daily_wall_limit_reached")
  ) {
    return DAILY_WALL_LIMIT_SENTINEL;
  }

  if (error?.code === "WALL_SCAN_QUOTA_EXCEEDED" || normalized.includes("wall_scan_quota_exceeded")) {
    const remaining = Number(error?.quota?.scansRemaining ?? 0);
    if (language === "fr") {
      return `Soumettre au Mur utilise ${WALL_SUBMISSION_SCAN_COST} scans, et il t'en reste ${remaining} aujourd'hui. Reviens demain pour soumettre.`;
    }
    return `Submitting to the Wall uses ${WALL_SUBMISSION_SCAN_COST} scans, and you have ${remaining} left today. Come back tomorrow to submit.`;
  }

  if (!raw) {
    return language === "fr"
      ? "Impossible d'envoyer au Mur pour le moment. Réessaie dans un instant."
      : "Could not submit to Le Mur right now. Please retry in a moment.";
  }

  if (normalized.includes("consent_required")) {
    return language === "fr"
      ? "Accepte le consentement de partage pour continuer."
      : "Please accept sharing consent to continue.";
  }

  if (normalized.includes("auth_required") || normalized.includes("auth_mismatch")) {
    return language === "fr"
      ? "Ta session a expiré. Reconnecte-toi puis réessaie."
      : "Your session expired. Please sign in again and retry.";
  }

  if (normalized.includes("too large")) {
    return language === "fr"
      ? "Ton image est un peu trop lourde (max 20 MB). Essaie une photo plus légère."
      : "Your image is too large (max 20 MB). Please try a lighter photo.";
  }

  if (normalized.includes("schema cache") || normalized.includes("could not find the")) {
    return language === "fr"
      ? "Petit souci technique temporaire pendant l'envoi. Ton résultat est bien conservé, réessaie dans un instant."
      : "Temporary technical issue while sending. Your result is safe, please retry in a moment.";
  }

  if (
    normalized.includes("insert_failed") ||
    normalized.includes("upload_failed") ||
    normalized.includes("permission denied") ||
    normalized.includes("row-level security")
  ) {
    return language === "fr"
      ? "Impossible d'envoyer au Mur pour le moment. Ton coloriage reste bien gardé dans ton parcours."
      : "Could not send to Le Mur right now. Your coloring is still safely kept in your flow.";
  }

  return language === "fr"
    ? "Impossible d'envoyer au Mur pour le moment. Réessaie un peu plus tard."
    : "Could not send to Le Mur right now. Please try again a bit later.";
}

function sanitizeFileName(fileName = "texture-upload.jpg") {
  return fileName
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9._-]/g, "");
}

function isAbsoluteUrl(value) {
  return /^https?:\/\//i.test(value || "");
}

function resolveStoredImagePath(value) {
  if (!value || isAbsoluteUrl(value)) return null;
  return String(value).trim() || null;
}

function nowIso() {
  return new Date().toISOString();
}

function computeWallExpiryDate(baseDate = new Date()) {
  const expiresAt = new Date(baseDate);
  expiresAt.setDate(expiresAt.getDate() + WALL_VISIBILITY_WINDOW_DAYS);
  return expiresAt.toISOString();
}

function normalizeSkillLevel(value) {
  const raw = String(value || "").trim().toLowerCase();
  const mapped = SKILL_LEVEL_ALIASES[raw] || raw;
  return SKILL_LEVELS.has(mapped) ? mapped : "intermediate";
}

function normalizeArtworkTitle(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return null;
  return normalized.slice(0, MAX_ARTWORK_TITLE_LENGTH);
}

function normalizeMonthlyCategory(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["beginner", "intermediate", "expert", "general"].includes(normalized)) {
    return normalized;
  }
  return "general";
}

function normalizeWallStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return WALL_STATUSES.has(normalized) ? normalized : "pending";
}

function normalizeMonthlyFeatureStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return MONTHLY_FEATURE_STATUSES.has(normalized) ? normalized : "none";
}

function normalizeMonthlyChallengeStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return MONTHLY_CHALLENGE_STATUSES.has(normalized) ? normalized : "none";
}

function normalizeMonthlyChallengeCategory(value) {
  return normalizeMonthlyCategory(value);
}

function normalizeSelectionType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return SELECTION_TYPES.has(normalized) ? normalized : "general";
}

function isRpcMissingError(error, rpcName) {
  const normalized = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toUpperCase();
  return (
    code === "42883" ||
    normalized.includes(`function public.${String(rpcName || "").toLowerCase()}`) ||
    normalized.includes("does not exist")
  );
}

function isValidYearMonth(value) {
  return /^\d{4}-\d{2}$/.test(String(value || "").trim());
}

function normalizeTikTokUsernameValue(value) {
  let raw = String(value || "").trim();
  if (!raw) return "";

  raw = raw
    .replace(/^https?:\/\/(www\.)?tiktok\.com\/@/i, "")
    .replace(/^@+/, "")
    .replace(/[/?#&].*$/, "")
    .trim();

  return raw;
}

function validateTikTokUsernameOrThrow(value, language = "fr") {
  const normalized = normalizeTikTokUsernameValue(value);
  if (!normalized) return "";

  if (!TIKTOK_USERNAME_REGEX.test(normalized)) {
    throw new Error(
      language === "fr"
        ? "Nom TikTok invalide. Utilise seulement lettres, chiffres, point ou underscore (2 a 24 caracteres)."
        : "Invalid TikTok username. Use letters, numbers, dots, or underscores only (2 to 24 characters)."
    );
  }

  return normalized;
}

function toYearMonth(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = parseIsoDate(value);
  if (!parsed) return null;
  return parsed.toISOString().slice(0, 7);
}

export function getPreviousMonthKey(baseDate = new Date()) {
  const date = new Date(baseDate);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth(); // 0-based
  const previous = new Date(Date.UTC(year, month - 1, 1, 12, 0, 0));
  const monthValue = String(previous.getUTCMonth() + 1).padStart(2, "0");
  return `${previous.getUTCFullYear()}-${monthValue}`;
}

function resolveSelectedMonthValue(selectedMonth, fallbackDate) {
  if (isValidYearMonth(selectedMonth)) {
    return String(selectedMonth).trim();
  }
  if (fallbackDate === null || fallbackDate === undefined || fallbackDate === "") {
    return null;
  }
  return toYearMonth(fallbackDate);
}

function resolveSelectedOrderingDate(row) {
  const selectedAt = parseIsoDate(row?.selected_at);
  if (selectedAt) return selectedAt;
  const legacySelectedAt = parseIsoDate(row?.monthly_challenge_selected_at);
  if (legacySelectedAt) return legacySelectedAt;
  const createdAt = parseIsoDate(row?.created_at);
  return createdAt || new Date(0);
}

export function formatSelectionMonthLabel(value, language = "fr") {
  let yearMonth = String(value || "").trim();

  if (!isValidYearMonth(yearMonth)) {
    yearMonth = toYearMonth(value);
  }

  if (!isValidYearMonth(yearMonth)) {
    return language === "fr" ? "Mois a venir" : "Coming soon";
  }

  const [year, month] = yearMonth.split("-");
  const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, 1, 12, 0, 0));
  if (Number.isNaN(parsed.getTime())) return yearMonth;
  return parsed.toLocaleDateString(language === "fr" ? "fr-CA" : "en-CA", {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

export function formatMonthLabel(monthKey, language = "fr") {
  return formatSelectionMonthLabel(monthKey, language);
}

function isLegacySelectedEntry(row) {
  return (
    normalizeMonthlyChallengeStatus(row?.monthly_challenge_status) === "selected" ||
    normalizeMonthlyFeatureStatus(row?.monthly_feature_status) === "selected"
  );
}

function matchesSelectionType(row, selectionType) {
  const normalizedSelectionType = normalizeSelectionType(selectionType);
  const rowSelectionType = String(row?.selection_type || "").trim().toLowerCase();
  if (rowSelectionType === normalizedSelectionType) return true;

  // Fallback for old rows that predate selection_type.
  if (!rowSelectionType) {
    if (normalizedSelectionType === "challenge") {
      return Boolean(row?.participates_in_monthly_challenge);
    }
    if (normalizedSelectionType === "contributor") {
      return Boolean(row?.submitted_by_contributor);
    }
    return !Boolean(row?.submitted_by_contributor);
  }

  return false;
}

function parseIsoDate(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function isProtectedWallSelection(row) {
  return (
    normalizeMonthlyChallengeStatus(row?.monthly_challenge_status) === "selected" ||
    normalizeMonthlyFeatureStatus(row?.monthly_feature_status) === "selected"
  );
}

function isProtectedWallExpiryValue(value) {
  const parsed = parseIsoDate(value);
  return Boolean(parsed && parsed.getUTCFullYear() >= 2999);
}

function normalizeScanType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "fridge_only") return "fridge_only";
  if (normalized === "ai_analysis") return "ai_analysis";
  if (!normalized) return null;
  if (normalized === "general-scan" || normalized === "general_scan") return "general_scan";
  if (normalized === "real") return "real";
  return normalized;
}

function isCurrentMonthlyChallengeParticipant(row, monthlyChallengeKey) {
  const resolvedKey = resolveCurrentMonthlyChallengeKey(monthlyChallengeKey);
  return (
    Boolean(row?.participates_in_monthly_challenge) &&
    String(row?.monthly_challenge_key || "").trim() === resolvedKey
  );
}

export function getWallEntrySection(row, { monthlyChallengeKey = MONTHLY_CHALLENGE_KEY } = {}) {
  if (!row) return null;
  if (!row?.image_url) return null;
  if (row?.removed_from_wall_at) return null;

  const wallStatus = normalizeWallStatus(row?.wall_status || row?.showcase_status);
  const showcaseStatus = normalizeWallStatus(row?.showcase_status || row?.wall_status);
  const isExplicitlyHidden =
    wallStatus === "removed"
    || wallStatus === "expired"
    || showcaseStatus === "removed"
    || showcaseStatus === "expired";
  const isApproved =
    wallStatus === "approved"
    || showcaseStatus === "approved";

  if (isExplicitlyHidden) return null;
  if (!isApproved) return null;
  if (!row?.showcase_consent) return null;

  const scanType = normalizeScanType(row?.scan_type);
  if (scanType === "fridge_only") return null;

  if (isCurrentMonthlyChallengeParticipant(row, monthlyChallengeKey)) {
    return "monthly";
  }

  return "free";
}

export function filterPublicWallEntries(rows, { section = "all", monthlyChallengeKey = MONTHLY_CHALLENGE_KEY } = {}) {
  const normalizedSection = String(section || "all").trim().toLowerCase();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const resolvedSection = getWallEntrySection(row, { monthlyChallengeKey });
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.info("[WALL_FILTER]", {
        id: row?.id || null,
        name: row?.showcase_name || null,
        wall_status: row?.wall_status || null,
        showcase_status: row?.showcase_status || null,
        showcase_consent: Boolean(row?.showcase_consent),
        scan_type: row?.scan_type || null,
        wall_expires_at: row?.wall_expires_at || null,
        section: resolvedSection,
      });
    }
    if (!resolvedSection) return false;
    if (normalizedSection === "all") return true;
    return resolvedSection === normalizedSection;
  });
}

async function resolveContributorUserIds(userIds = []) {
  const normalizedIds = Array.from(
    new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (!normalizedIds.length) {
    return new Set();
  }

  // Le recalcul passe par la RPC security definer get_contributor_user_ids :
  // les tables subscriptions / supporters ont une RLS *_select_own qui empeche
  // le client frontend de lire le statut des autres utilisateurs. La RPC
  // bypasse la RLS et reutilise is_user_current_contributor cote SQL.
  try {
    const { data, error } = await supabase.rpc("get_contributor_user_ids", {
      p_user_ids: normalizedIds,
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[SHOWCASE_CONTRIBUTOR] rpc get_contributor_user_ids failed", {
        message: error?.message || "unknown",
      });
      return new Set();
    }

    const rows = Array.isArray(data) ? data : [];
    const contributorIds = new Set();
    rows.forEach((row) => {
      const userId = String(row?.user_id || "").trim();
      if (userId) {
        contributorIds.add(userId);
      }
    });
    return contributorIds;
  } catch (_error) {
    // Best effort only; fallback stays on persisted submitted_by_contributor.
    // eslint-disable-next-line no-console
    console.warn("[SHOWCASE_CONTRIBUTOR] rpc get_contributor_user_ids threw");
    return new Set();
  }
}

/**
 * Comme resolveContributorUserIds, mais retourne une Map user_id -> contributor_form.
 * Les cles de la Map SONT les contributeurs actifs. La valeur est leur forme
 * choisie ('contributrice' | 'contributeur' | 'contribue') ou null si pas encore choisie.
 * Passe par la RPC security definer get_contributor_user_ids (bypasse la RLS profiles).
 */
async function resolveContributorForms(userIds = []) {
  const normalizedIds = Array.from(
    new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (!normalizedIds.length) {
    return new Map();
  }

  try {
    const { data, error } = await supabase.rpc("get_contributor_user_ids", {
      p_user_ids: normalizedIds,
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[SHOWCASE_CONTRIBUTOR] rpc resolveContributorForms failed", {
        message: error?.message || "unknown",
      });
      return new Map();
    }

    const rows = Array.isArray(data) ? data : [];
    const formsByUserId = new Map();
    rows.forEach((row) => {
      const userId = String(row?.user_id || "").trim();
      if (userId) {
        formsByUserId.set(userId, row?.contributor_form || null);
      }
    });
    return formsByUserId;
  } catch (_error) {
    // eslint-disable-next-line no-console
    console.warn("[SHOWCASE_CONTRIBUTOR] rpc resolveContributorForms threw");
    return new Map();
  }
}

/**
 * CODEX_PROMPT_74 + PROMPT_77 : retourne un Set des user_ids dont le Frigo
 * est public.
 *
 * Migration PROMPT_77 : passe d'un SELECT direct sur `profiles` (bloque par la
 * RLS profiles - constate en prod, le selecteur etait silencieusement vide)
 * a un appel RPC security-definer `get_frigo_public_user_ids(p_user_ids[])`
 * qui bypasse la RLS et ne retourne QUE les user_ids dont frigo_is_public=true.
 * Pattern miroir de get_contributor_user_ids deja en place.
 */
async function resolveFrigoPublicUserIds(userIds = []) {
  const normalizedIds = Array.from(
    new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (!normalizedIds.length) {
    return new Set();
  }

  try {
    const { data, error } = await supabase.rpc("get_frigo_public_user_ids", {
      p_user_ids: normalizedIds,
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[SHOWCASE_FRIGO] rpc get_frigo_public_user_ids failed", {
        message: error?.message || "unknown",
      });
      return new Set();
    }

    const rows = Array.isArray(data) ? data : [];
    const set = new Set();
    rows.forEach((row) => {
      // CODEX_PROMPT_77 : la RPC retourne des lignes { user_id: uuid }.
      const id = String(row?.user_id || "").trim();
      if (id) set.add(id);
    });
    return set;
  } catch (_error) {
    // eslint-disable-next-line no-console
    console.warn("[SHOWCASE_FRIGO] resolveFrigoPublicUserIds threw");
    return new Set();
  }
}

async function resolveSubmissionContributorFlag(userId, explicitFlag = false) {
  if (explicitFlag) return true;
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return false;
  const resolved = await resolveContributorUserIds([normalizedUserId]);
  return resolved.has(normalizedUserId);
}

async function resolveAdminReactionUserIds(userIds = []) {
  const normalizedIds = Array.from(
    new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (!normalizedIds.length) {
    return new Set();
  }

  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, role, is_admin, admin")
      .in("id", normalizedIds);

    if (error || !Array.isArray(data)) {
      return new Set();
    }

    return new Set(
      data
        .filter((row) => {
          if (!row?.id) return false;
          return isAdminProfile(row);
        })
        .map((row) => String(row.id).trim())
        .filter(Boolean)
    );
  } catch (_error) {
    return new Set();
  }
}

function applyAdminReactionWeight(countsBySubmissionId, reactionRows = [], adminUserIds = new Set()) {
  if (!(adminUserIds instanceof Set) || adminUserIds.size === 0) {
    return countsBySubmissionId;
  }

  const weightedCounts = { ...countsBySubmissionId };
  (Array.isArray(reactionRows) ? reactionRows : []).forEach((row) => {
    const submissionId = String(row?.submission_id || "").trim();
    const userId = String(row?.user_id || "").trim();
    if (!submissionId || !userId) return;
    if (!adminUserIds.has(userId)) return;
    weightedCounts[submissionId] = Number(weightedCounts[submissionId] || 0) + (ADMIN_REACTION_WEIGHT - 1);
  });

  return weightedCounts;
}

function resolveCurrentMonthlyChallengeKey(value) {
  const normalized = String(value || "").trim();
  return normalized || MONTHLY_CHALLENGE_KEY;
}

function isAiScoreStarWorthy(score) {
  return Number(score) >= 80;
}

function validateTextureImageFile(file) {
  if (!file) {
    throw new Error("No file provided for upload.");
  }

  if (file.size > MAX_TEXTURE_IMAGE_SIZE_BYTES) {
    throw new Error("Image file is too large. Max allowed size is 20 MB.");
  }

  if (!ALLOWED_IMAGE_TYPES.has((file.type || "").toLowerCase())) {
    throw new Error("Only JPG, PNG, WEBP and HEIC image files are allowed.");
  }
}

async function ensureAuthenticatedSessionUser(expectedUserId) {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user?.id) {
    throw new Error("AUTH_REQUIRED: Please sign in again before sharing.");
  }
  if (expectedUserId && data.user.id !== expectedUserId) {
    throw new Error("AUTH_MISMATCH: Session user does not match payload user.");
  }
}

async function ensureAdminAccess() {
  const sessionState = await resolveCurrentSessionAdminState();

  if (!sessionState?.user) {
    throw new Error("Authentication required for admin actions.");
  }

  if (!sessionState?.isAdmin) {
    throw new Error("Admin access required.");
  }

  return sessionState;
}

function pickPublicWallPreviewFields(row) {
  return {
    id: row?.id || null,
    artwork_title: row?.artwork_title || null,
    showcase_name: row?.showcase_name || null,
    image_url: row?.image_url || null,
    image_display_url: row?.image_display_url || null,
    full_display_url: row?.full_display_url || null,
    home_display_url: row?.home_display_url || null,
    image_path: row?.image_path || resolveStoredImagePath(row?.image_url),
    thumbnail_storage_path: row?.thumbnail_storage_path || null,
    submitted_by_contributor: Boolean(row?.submitted_by_contributor),
    created_at: row?.created_at || null,
    wall_approved_at: row?.wall_approved_at || null,
  };
}

async function hydrateSubmissionImage(row, { preview = false, thumbnailOptions = null } = {}) {
  if (!row) return row;
  const fallbackImageUrl = row?.image_display_url || row?.image_url || null;

  let fullDisplayUrl = fallbackImageUrl;
  try {
    fullDisplayUrl = await resolveTextureImageUrl(row.image_url);
  } catch (_error) {
    fullDisplayUrl = fallbackImageUrl;
  }

  let imageDisplayUrl = fullDisplayUrl;
  if (preview) {
    try {
      imageDisplayUrl = await getShowcaseThumbnailUrl(row, thumbnailOptions || {});
    } catch (_error) {
      imageDisplayUrl = fullDisplayUrl;
    }
  }

  let thumbnailDisplayUrl = imageDisplayUrl;
  if (row.thumbnail_storage_path) {
    try {
      thumbnailDisplayUrl = await resolveTextureImageUrl(
        row.thumbnail_storage_path,
        SIGNED_URL_TTL_SECONDS
      );
    } catch (_error) {
      thumbnailDisplayUrl = imageDisplayUrl;
    }
  }

  return {
    ...row,
    image_path: resolveStoredImagePath(row.image_url),
    image_display_url: imageDisplayUrl || fullDisplayUrl || fallbackImageUrl,
    thumbnail_display_url: thumbnailDisplayUrl || imageDisplayUrl || fullDisplayUrl || fallbackImageUrl,
    full_display_url: fullDisplayUrl || fallbackImageUrl,
    home_display_url: fullDisplayUrl || fallbackImageUrl,
    featured_badge_80_plus: isAiScoreStarWorthy(row.ai_score),
  };
}

async function markExpiredWallEntriesBestEffort() {
  // Deprecated behavior:
  // We no longer auto-expire approved public wall entries by age.
  return Promise.resolve();
}

export function getTextureUploadConstraints() {
  return {
    maxSizeBytes: MAX_TEXTURE_IMAGE_SIZE_BYTES,
    allowedMimeTypes: Array.from(ALLOWED_IMAGE_TYPES)
  };
}

/**
 * Upload a texture image (compressed) + its thumbnail to Supabase Storage.
 * Returns { filePath, thumbnailPath } where thumbnailPath may be null on failure.
 *
 * cacheControl is set to 1 year (31536000 s) — files use unique timestamped
 * names so cache invalidation is never needed.
 */
export async function uploadTextureImage(file, userId) {
  if (!userId) {
    throw new Error("User ID is required to upload texture image.");
  }
  validateTextureImageFile(file);

  // Compress the main image before upload (max 1600px, quality 0.82).
  const compressed = await compressImageForUpload(file, {
    maxWidth: 1600,
    maxHeight: 1600,
    quality: 0.82,
  });

  const safeName = sanitizeFileName(compressed.name || file.name || "texture-upload.jpg");
  const timestamp = Date.now();
  const filePath = `${userId}/${timestamp}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(filePath, compressed, {
      upsert: false,
      contentType: compressed.type || "image/jpeg",
      cacheControl: "31536000",
    });

  if (uploadError) {
    throw new Error(`Image upload failed: ${uploadError.message}`);
  }

  // Upload thumbnail (max 500px, quality 0.78) — best effort, never blocks main upload.
  let thumbnailPath = null;
  try {
    const thumb = await createImageThumbnail(compressed, {
      maxWidth: 500,
      maxHeight: 500,
      quality: 0.78,
    });
    const thumbName = sanitizeFileName(thumb.name || `${timestamp}-thumb.jpg`);
    const thumbPath = `${userId}/thumbs/${timestamp}-${thumbName}`;

    const { error: thumbError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(thumbPath, thumb, {
        upsert: false,
        contentType: thumb.type || "image/jpeg",
        cacheControl: "31536000",
      });

    if (!thumbError) {
      thumbnailPath = thumbPath;
    }
  } catch (_thumbErr) {
    // Thumbnail is best-effort; main upload succeeded.
  }

  return { filePath, thumbnailPath };
}

export async function resolveTextureImageUrl(
  storedValue,
  expiresIn = SIGNED_URL_TTL_SECONDS,
  { transform = null } = {}
) {
  if (!storedValue) return null;

  if (isAbsoluteUrl(storedValue)) {
    return storedValue;
  }

  // Build a stable cache key that incorporates any transform options.
  const cacheKey = transform
    ? `${storedValue}::w${transform.width || 0}::q${transform.quality || 0}::${transform.format || ""}::${transform.resize || ""}`
    : storedValue;

  // Return cached URL if still valid.
  const cached = signedUrlCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  const storage = supabase.storage.from(BUCKET_NAME);

  if (transform) {
    try {
      const { data, error } = await storage.createSignedUrl(storedValue, expiresIn, {
        transform,
      });

      if (!error && data?.signedUrl) {
        signedUrlCache.set(cacheKey, { url: data.signedUrl, expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS });
        return data.signedUrl;
      }
    } catch (_error) {
      // Transformation support is optional; fall back to the original asset below.
    }
  }

  const { data, error } = await storage.createSignedUrl(storedValue, expiresIn);

  if (error) {
    throw new Error(`Could not generate signed image URL: ${error.message}`);
  }

  const signedUrl = data?.signedUrl || null;
  if (signedUrl) {
    signedUrlCache.set(cacheKey, { url: signedUrl, expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS });
  }
  return signedUrl;
}

export async function getShowcaseThumbnailUrl(entry, options = {}) {
  const storedValue =
    resolveStoredImagePath(entry?.thumbnail_storage_path)
    || resolveStoredImagePath(entry?.image_path)
    || resolveStoredImagePath(entry?.image_url)
    || null;
  const fallbackUrl = entry?.image_display_url || entry?.image_url || null;

  if (!storedValue) {
    return fallbackUrl;
  }

  try {
    return await resolveTextureImageUrl(storedValue, SIGNED_URL_TTL_SECONDS, {
      transform: {
        width: Number.isFinite(Number(options.width)) ? Number(options.width) : 520,
        quality: Number.isFinite(Number(options.quality)) ? Number(options.quality) : 72,
        resize: options.resize || "cover",
        format: options.format || "webp",
      },
    });
  } catch (_error) {
    try {
      return await resolveTextureImageUrl(storedValue);
    } catch (__error) {
      return fallbackUrl;
    }
  }
}

export async function submitTextureShowcase({
  file,
  userId,
  textureType,
  aiScore,
  artworkTitle = "",
  submittedByContributor = false,
  declaredSkillLevel = "intermediate",
  showcaseName = "",
  showcaseConsent = false,
  monthlyFeatureConsent = false,
  participatesInMonthlyChallenge = false,
  monthlyChallengeKey = MONTHLY_CHALLENGE_KEY,
}) {
  if (!file) {
    throw new Error("A texture image file is required.");
  }
  if (!userId) {
    throw new Error("User must be authenticated to submit showcase.");
  }
  if (!textureType) {
    throw new Error("Texture type is required.");
  }
  if (!showcaseConsent) {
    throw new Error("CONSENT_REQUIRED: Showcase consent is required.");
  }

  let resolvedContributorFlag = Boolean(submittedByContributor);

  try {
    await ensureAuthenticatedSessionUser(userId);

    // Wall submissions are FREE — the publication right is already included
    // in the Analyse IA cost (1 scan). When wallSubmissionScanCost is 0 we
    // skip the quota call entirely; bumping it back up is a single config
    // change in `config/kofi.js`.
    let postSubmitConsumed = false;
    if (WALL_SUBMISSION_SCAN_COST > 0) {
      try {
        await consumeAIScanQuota({ scanCost: WALL_SUBMISSION_SCAN_COST, language: "fr" });
        postSubmitConsumed = true;
      } catch (quotaError) {
        // Surface a quota-typed error so callers can render the cozy "out of scans" UI.
        if (quotaError?.quotaExceeded) {
          const wallError = new Error("WALL_SCAN_QUOTA_EXCEEDED");
          wallError.code = "WALL_SCAN_QUOTA_EXCEEDED";
          wallError.quotaExceeded = true;
          wallError.quota = quotaError.quota;
          throw wallError;
        }
        throw quotaError;
      }
    }

    resolvedContributorFlag = await resolveSubmissionContributorFlag(
      userId,
      Boolean(submittedByContributor)
    );

    let storagePath = "";
    let thumbnailStoragePath = null;
    try {
      const uploadResult = await uploadTextureImage(file, userId);
      storagePath = uploadResult.filePath;
      thumbnailStoragePath = uploadResult.thumbnailPath || null;
    } catch (error) {
      // Note: the 2 scans were already consumed. Refund-on-failure isn't
      // exposed via a public endpoint today; we accept the rare cost of a
      // failed upload to keep enforcement simple. postSubmitConsumed flag
      // is intentionally retained for future use.
      void postSubmitConsumed;
      throw new Error(`UPLOAD_FAILED: ${error.message}`);
    }

    const payload = {
      user_id: userId,
      showcase_name: showcaseName.trim(),
      artwork_title: normalizeArtworkTitle(artworkTitle),
      submitted_by_contributor: resolvedContributorFlag,
      showcase_consent: Boolean(showcaseConsent),
      showcase_status: "pending",
      wall_status: "pending",
      wall_approved_at: null,
      wall_expires_at: null,
      removed_from_wall_at: null,
      removed_from_wall_by: null,
      monthly_feature_consent: Boolean(monthlyFeatureConsent || participatesInMonthlyChallenge),
      monthly_feature_status: "none",
      monthly_feature_category: normalizeSkillLevel(declaredSkillLevel),
      monthly_challenge_key: Boolean(participatesInMonthlyChallenge)
        ? resolveCurrentMonthlyChallengeKey(monthlyChallengeKey)
        : null,
      participates_in_monthly_challenge: Boolean(participatesInMonthlyChallenge),
      monthly_challenge_status: Boolean(participatesInMonthlyChallenge) ? "participant" : "none",
      monthly_challenge_category: normalizeSkillLevel(declaredSkillLevel),
      monthly_challenge_selected_at: null,
      monthly_challenge_xp_bonus_awarded: false,
      monthly_challenge_xp_bonus_awarded_at: null,
      monthly_challenge_level_up_awarded: false,
      monthly_challenge_level_up_awarded_at: null,
      selection_type: null,
      selected_month: null,
      selected_at: null,
      challenge_theme: null,
      spotlight_note: null,
      declared_skill_level: normalizeSkillLevel(declaredSkillLevel),
      scan_type: "ai_analysis",
      texture_type: textureType,
      image_url: storagePath,
      thumbnail_storage_path: thumbnailStoragePath,
      ai_score: Number.isFinite(Number(aiScore)) ? Number(aiScore) : null,
      created_at: nowIso()
    };

    const corePayload = buildCoreInsertPayload(payload);
    const coreSelectCols = CORE_SELECT_COLUMNS.join(",\n  ");

    const { data, error } = await runWithSchemaFallback(
      () =>
        supabase
          .from(TABLE_NAME)
          .insert(stripIgnoredSchemaColumns(payload))
          .select(getSelectColumns())
          .single(),
      "submit_texture_showcase_insert",
      24,
      () =>
        supabase
          .from(TABLE_NAME)
          .insert(corePayload)
          .select(coreSelectCols)
          .single()
    );

    if (error) {
      throw new Error(`INSERT_FAILED: ${error.message}`);
    }

    let monthlyChallengeXpBonusGranted = 0;
    if (payload.participates_in_monthly_challenge) {
      const { data: bonusData, error: bonusError } = await supabase.rpc("colobongo_award_monthly_participation_xp", {
        p_submission_id: String(data.id),
      });
      if (bonusError) {
        // eslint-disable-next-line no-console
        console.warn("[MONTHLY_CHALLENGE] could not award participation XP", {
          submissionId: data.id,
          message: bonusError?.message || "unknown",
        });
      } else {
        monthlyChallengeXpBonusGranted = Number(bonusData) || 0;
      }
    }

    return {
      ...data,
      monthly_challenge_xp_bonus_granted: monthlyChallengeXpBonusGranted,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[SHOWCASE_SUBMIT] submitTextureShowcase failed", {
      userId,
      textureType,
      aiScore: Number.isFinite(Number(aiScore)) ? Number(aiScore) : null,
      declaredSkillLevel: normalizeSkillLevel(declaredSkillLevel),
      artworkTitle: normalizeArtworkTitle(artworkTitle),
      submittedByContributor: Boolean(submittedByContributor),
      resolvedSubmittedByContributor: Boolean(resolvedContributorFlag),
      hasConsent: Boolean(showcaseConsent),
      monthlyFeatureConsent: Boolean(monthlyFeatureConsent),
      participatesInMonthlyChallenge: Boolean(participatesInMonthlyChallenge),
      monthlyChallengeKey: resolveCurrentMonthlyChallengeKey(monthlyChallengeKey),
      message: error?.message || "unknown"
    });
    throw new Error(error.message || "Unexpected error while submitting showcase.");
  }
}

/**
 * Admin-only helper: enriches submission rows with the submitter's email.
 *
 * Calls the `get_showcase_admin_emails` Postgres RPC (SECURITY DEFINER).
 * That function joins auth.users server-side and raises an exception if the
 * calling session is not an admin.
 *
 * This function is intentionally never called from fetchPublicWallEntries()
 * or any other public path — only from fetchTextureShowcaseSubmissions().
 *
 * On any error (network, insufficient_privilege, etc.) it returns the rows
 * unchanged rather than throwing, so that the admin UI still loads without email.
 */
async function enrichAdminSubmissionsWithEmails(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;

  const submissionIds = rows
    .map((r) => r?.id)
    .filter((id) => typeof id === "string" && id.length > 0);

  if (submissionIds.length === 0) return rows;

  try {
    const { data, error } = await supabase.rpc("get_showcase_admin_emails", {
      submission_ids: submissionIds,
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[ADMIN_EMAIL] could not enrich submissions with emails", {
        code: error?.code,
        message: error?.message,
      });
      return rows;
    }

    // Build a lookup map: submission_id → submitter_email
    const emailById = (Array.isArray(data) ? data : []).reduce((acc, item) => {
      if (item?.submission_id && item?.submitter_email) {
        acc[item.submission_id] = String(item.submitter_email).trim();
      }
      return acc;
    }, {});

    return rows.map((row) => ({
      ...row,
      submitter_email: emailById[row?.id] || null,
    }));
  } catch (unexpectedError) {
    // eslint-disable-next-line no-console
    console.warn("[ADMIN_EMAIL] unexpected error enriching emails", {
      reason: unexpectedError?.message || "unknown",
    });
    return rows;
  }
}

async function enrichAdminSubmissionsWithEmailFallback(rows) {
  const baseRows = await enrichAdminSubmissionsWithEmails(rows);
  const safeRows = Array.isArray(baseRows) ? baseRows : (Array.isArray(rows) ? rows : []);
  if (!safeRows.length) return safeRows;

  const unresolvedRows = safeRows.filter((row) => !row?.submitter_email);
  const unresolvedUserIds = Array.from(
    new Set(
      unresolvedRows
        .map((row) => String(row?.user_id || "").trim())
        .filter(Boolean)
    )
  );

  if (!unresolvedUserIds.length) {
    return safeRows;
  }

  try {
    const { data: profileRows, error: profileError } = await supabase
      .from("profiles")
      .select("id, email")
      .in("id", unresolvedUserIds);

    if (profileError) {
      // eslint-disable-next-line no-console
      console.warn("[ADMIN_EMAIL] profiles fallback lookup failed", {
        code: profileError?.code,
        message: profileError?.message,
      });
      return safeRows;
    }

    const emailByUserId = (Array.isArray(profileRows) ? profileRows : []).reduce((acc, item) => {
      const userId = String(item?.id || "").trim();
      const email = String(item?.email || "").trim().toLowerCase();
      if (!userId || !email) return acc;
      acc[userId] = email;
      return acc;
    }, {});

    const mergedRows = safeRows.map((row) => {
      const currentEmail = String(row?.submitter_email || "").trim();
      if (currentEmail) {
        return {
          ...row,
          submitter_email: currentEmail,
        };
      }

      const userId = String(row?.user_id || "").trim();
      const fallbackEmail = emailByUserId[userId];
      return {
        ...row,
        submitter_email: fallbackEmail || null,
      };
    });

    const unresolvedCount = mergedRows.reduce(
      (count, row) => (row?.submitter_email ? count : count + 1),
      0
    );
    if (unresolvedCount > 0) {
      // eslint-disable-next-line no-console
      console.warn("[ADMIN_EMAIL] some submission emails remain unresolved", {
        unresolvedCount,
        totalRows: mergedRows.length,
      });
    }

    return mergedRows;
  } catch (unexpectedError) {
    // eslint-disable-next-line no-console
    console.warn("[ADMIN_EMAIL] profiles fallback unexpected error", {
      reason: unexpectedError?.message || "unknown",
    });
    return safeRows;
  }
}

export async function fetchTextureShowcaseSubmissions() {
  await ensureAdminAccess();
  await markExpiredWallEntriesBestEffort();

  const { data, error } = await runWithSchemaFallback(
    () =>
      supabase
        .from(TABLE_NAME)
        .select(getSelectColumns())
        .order("created_at", { ascending: false }),
    "fetch_texture_showcase_submissions",
    24,
    () =>
      supabase
        .from(TABLE_NAME)
        .select(CORE_SELECT_COLUMNS.join(",\n  "))
        .order("created_at", { ascending: false })
  );

  if (error) {
    throw new Error(`Could not fetch showcase submissions: ${error.message}`);
  }

  const rows = Array.isArray(data) ? data : [];
  const contributorIds = await resolveContributorUserIds(rows.map((row) => row?.user_id));
  const normalizedRows = rows.map((row) => ({
    ...row,
    artwork_title: row?.artwork_title ?? null,
    submitted_by_contributor: Boolean(row?.submitted_by_contributor),
    wall_status: row?.wall_status || row?.showcase_status || "pending",
    wall_approved_at: row?.wall_approved_at ?? null,
    wall_expires_at: row?.wall_expires_at ?? null,
    removed_from_wall_at: row?.removed_from_wall_at ?? null,
    removed_from_wall_by: row?.removed_from_wall_by ?? null,
    monthly_challenge_key: row?.monthly_challenge_key ?? null,
    participates_in_monthly_challenge: Boolean(row?.participates_in_monthly_challenge),
    monthly_challenge_status: row?.monthly_challenge_status || "none",
    declared_skill_level: row?.declared_skill_level || "intermediate",
    thumbnail_storage_path: row?.thumbnail_storage_path ?? null,
    selection_type: row?.selection_type ?? null,
    selected_month: row?.selected_month ?? null,
    selected_at: row?.selected_at ?? null,
    challenge_theme: row?.challenge_theme ?? null,
    challenge_name: row?.challenge_name ?? null,
    monthly_challenge_title: row?.monthly_challenge_title ?? null,
    spotlight_note: row?.spotlight_note ?? null,
    spotlight_tiktok_username: row?.spotlight_tiktok_username ?? null,
    spotlight_tiktok_updated_at: row?.spotlight_tiktok_updated_at ?? null,
    contributor_active:
      Boolean(row?.submitted_by_contributor) ||
      contributorIds.has(String(row?.user_id || "").trim()),
  }));
  const hydratedRows = await Promise.all(normalizedRows.map((row) => hydrateSubmissionImage(row)));

  // Enrich admin view only — never called from public path
  return enrichAdminSubmissionsWithEmailFallback(hydratedRows);
}

export async function updateTextureWallStatus(submissionId, nextStatus) {
  const adminState = await ensureAdminAccess();
  const normalizedStatus = normalizeWallStatus(nextStatus);
  const nowTimestamp = nowIso();
  const adminIdentifier = adminState?.email || adminState?.user?.id || "admin";

  const payload = {
    wall_status: normalizedStatus,
  };

  if (normalizedStatus === "approved") {
    payload.showcase_status = "approved";
    payload.wall_approved_at = nowTimestamp;
    payload.wall_expires_at = null;
    payload.removed_from_wall_at = null;
    payload.removed_from_wall_by = null;
  }

  if (normalizedStatus === "removed") {
    payload.removed_from_wall_at = nowTimestamp;
    payload.removed_from_wall_by = adminIdentifier;
    payload.monthly_feature_status = "none";
    payload.monthly_challenge_status = "none";
    payload.monthly_challenge_selected_at = null;
    payload.selection_type = null;
    payload.selected_month = null;
    payload.selected_at = null;
    payload.challenge_theme = null;
    payload.spotlight_note = null;
  }

  if (normalizedStatus === "pending") {
    payload.showcase_status = "pending";
    payload.wall_approved_at = null;
    payload.wall_expires_at = null;
    payload.removed_from_wall_at = null;
    payload.removed_from_wall_by = null;
    payload.monthly_challenge_selected_at = null;
    payload.selection_type = null;
    payload.selected_month = null;
    payload.selected_at = null;
    payload.challenge_theme = null;
    payload.spotlight_note = null;
  }

  const { data, error } = await runWithSchemaFallback(
    () =>
      supabase
        .from(TABLE_NAME)
        .update(stripIgnoredSchemaColumns(payload))
        .eq("id", submissionId)
        .select(getSelectColumns())
        .single(),
    "update_texture_wall_status"
  );

  if (error) {
    throw new Error(`Could not update wall status: ${error.message}`);
  }

  if (!data) {
    return data;
  }

  return hydrateSubmissionImage(data);
}

export async function updateTextureShowcaseStatus(submissionId, nextStatus) {
  if (String(nextStatus || "").toLowerCase() === "rejected") {
    return updateTextureWallStatus(submissionId, "removed");
  }
  return updateTextureWallStatus(submissionId, nextStatus);
}

export async function updateTextureMonthlyFeatureStatus(
  submissionId,
  nextStatus,
  monthlyFeatureCategory = "general"
) {
  return updateTextureMonthlyChallengeStatus(submissionId, nextStatus, monthlyFeatureCategory);
}

/**
 * Removes a submission from the monthly challenge by resetting its status to "none".
 * Used by admins to reject / un-select a monthly challenge entry.
 */
export async function rejectMonthlyChallengeEntry(submissionId) {
  return updateTextureMonthlyChallengeStatus(submissionId, "none", "general", MONTHLY_CHALLENGE_KEY, {
    clearSpotlight: false,
  });
}

export async function archiveMonthlyChallengeEntries({
  monthlyChallengeKey = MONTHLY_CHALLENGE_KEY,
  protectedSelectedMonth = null,
} = {}) {
  await ensureAdminAccess();
  const resolvedKey = resolveCurrentMonthlyChallengeKey(monthlyChallengeKey);
  const normalizedProtectedMonth = resolveSelectedMonthValue(
    protectedSelectedMonth,
    getPreviousMonthKey()
  );

  const { data: rows, error: rowsError } = await runWithSchemaFallback(
    () =>
      supabase
        .from(TABLE_NAME)
        .select("id, selection_type, selected_month, selected_at")
        .eq("monthly_challenge_key", resolvedKey)
        .eq("participates_in_monthly_challenge", true),
    "archive_monthly_challenge_entries_load_rows"
  );

  if (rowsError) {
    throw new Error(`Could not load monthly challenge rows: ${rowsError.message}`);
  }

  const eligibleRows = (Array.isArray(rows) ? rows : []).filter((row) => {
    const isProtectedChallengePick =
      String(row?.selection_type || "").trim().toLowerCase() === "challenge"
      && String(row?.selected_month || "").trim() === String(normalizedProtectedMonth || "").trim()
      && Boolean(row?.selected_at);
    return !isProtectedChallengePick;
  });

  const targetIds = eligibleRows
    .map((row) => String(row?.id || "").trim())
    .filter(Boolean);

  if (!targetIds.length) {
    return {
      updatedCount: 0,
      monthlyChallengeKey: resolvedKey,
      protectedSelectedMonth: normalizedProtectedMonth,
    };
  }

  const archivePayload = stripIgnoredSchemaColumns({
    participates_in_monthly_challenge: false,
    monthly_challenge_status: "none",
    monthly_challenge_selected_at: null,
    monthly_feature_status: "none",
  });

  const { data: archivedRows, error: archiveError } = await runWithSchemaFallback(
    () =>
      supabase
        .from(TABLE_NAME)
        .update(archivePayload)
        .in("id", targetIds)
        .select("id"),
    "archive_monthly_challenge_entries_update_rows"
  );

  if (archiveError) {
    throw new Error(`Could not archive monthly challenge entries: ${archiveError.message}`);
  }

  return {
    updatedCount: Array.isArray(archivedRows) ? archivedRows.length : targetIds.length,
    monthlyChallengeKey: resolvedKey,
    protectedSelectedMonth: normalizedProtectedMonth,
  };
}

export async function updateTextureMonthlyChallengeStatus(
  submissionId,
  nextStatus,
  monthlyChallengeCategory = "general",
  monthlyChallengeKey = MONTHLY_CHALLENGE_KEY,
  options = {}
) {
  await ensureAdminAccess();
  const nowTimestamp = nowIso();
  const normalizedStatus = normalizeMonthlyChallengeStatus(nextStatus);
  const normalizedCategory = normalizeMonthlyChallengeCategory(monthlyChallengeCategory);
  const resolvedKey = resolveCurrentMonthlyChallengeKey(monthlyChallengeKey);
  const normalizedSelectionType = normalizeSelectionType(options?.selectionType);
  const allowAnyApprovedWallEntry = Boolean(options?.allowAnyApprovedWallEntry);
  const clearSpotlight = Boolean(options?.clearSpotlight);
  const normalizedChallengeTheme = String(options?.challengeTheme || "").trim() || null;
  const normalizedSpotlightNote = String(options?.spotlightNote || "").trim() || null;
  const resolvedSelectedAt = normalizedStatus === "selected" ? nowTimestamp : null;
  const resolvedSelectedMonth = normalizedStatus === "selected"
    ? resolveSelectedMonthValue(
        options?.selectedMonth || getPreviousMonthKey(nowTimestamp),
        resolvedSelectedAt || nowTimestamp
      )
    : null;
  const { data: currentSubmission, error: currentSubmissionError } = await runWithSchemaFallback(
    () =>
      supabase
        .from(TABLE_NAME)
        .select(getSelectColumns())
        .eq("id", submissionId)
        .single(),
    "get_texture_monthly_challenge_submission"
  );

  if (currentSubmissionError) {
    throw new Error(`Could not load current monthly challenge submission: ${currentSubmissionError.message}`);
  }

  const currentWallStatus = normalizeWallStatus(
    currentSubmission?.wall_status || currentSubmission?.showcase_status
  );
  const shouldRestoreTimedExpiry =
    currentWallStatus === "approved" &&
    normalizedStatus !== "selected" &&
    (isProtectedWallSelection(currentSubmission) ||
      isProtectedWallExpiryValue(currentSubmission?.wall_expires_at) ||
      !currentSubmission?.wall_expires_at);
  const mappedFeatureStatus =
    normalizedStatus === "selected"
      ? "selected"
      : normalizedStatus === "candidate"
        ? "candidate"
        : "none";

  const payload = {
    monthly_challenge_key: resolvedKey,
    monthly_challenge_status: normalizedStatus,
    monthly_challenge_category: normalizedCategory,
    monthly_challenge_selected_at: normalizedStatus === "selected" ? nowTimestamp : null,
    monthly_feature_status: normalizedStatus === "none" ? "none" : mappedFeatureStatus,
    monthly_feature_category: normalizedCategory,
    // Tient le flag de participation aligné avec le statut. Sans ça, "Retirer
    // du défi" depuis l'admin laissait le coloriage dans la section "Défi du
    // mois" du Mur, parce que getWallEntrySection -> isCurrentMonthlyChallenge
    // Participant teste participates_in_monthly_challenge.
    participates_in_monthly_challenge: normalizedStatus !== "none",
    selection_type: normalizedStatus === "selected"
      ? normalizedSelectionType
      : (clearSpotlight ? null : currentSubmission?.selection_type ?? null),
    selected_month: normalizedStatus === "selected"
      ? resolvedSelectedMonth
      : (clearSpotlight ? null : currentSubmission?.selected_month ?? null),
    selected_at: normalizedStatus === "selected"
      ? resolvedSelectedAt
      : (clearSpotlight ? null : currentSubmission?.selected_at ?? null),
    challenge_theme: normalizedStatus === "selected"
      ? normalizedChallengeTheme
      : (clearSpotlight ? null : currentSubmission?.challenge_theme ?? null),
    spotlight_note: normalizedStatus === "selected"
      ? normalizedSpotlightNote
      : (clearSpotlight ? null : currentSubmission?.spotlight_note ?? null),
  };

  if (normalizedStatus === "selected") {
    payload.wall_expires_at = PROTECTED_WALL_EXPIRY_DATE;
  } else if (shouldRestoreTimedExpiry) {
    payload.wall_expires_at = computeWallExpiryDate(new Date(nowTimestamp));
  }

  const { data, error } = await runWithSchemaFallback(() => {
    let query = supabase
      .from(TABLE_NAME)
      .update(stripIgnoredSchemaColumns(payload))
      .eq("id", submissionId);

    if (normalizedStatus === "candidate" || normalizedStatus === "selected") {
      query = query.eq("wall_status", "approved");
      if (!allowAnyApprovedWallEntry) {
        query = query.eq("participates_in_monthly_challenge", true);
      }
    }

    return query.select(getSelectColumns()).single();
  }, "update_texture_monthly_challenge_status");
  if (error) {
    throw new Error(`Could not update monthly challenge status: ${error.message}`);
  }

  let monthlyChallengeLevelXpGranted = 0;
  let latestData = data;
  if (normalizedStatus === "selected") {
    const { data: selectionRewardData, error: selectionRewardError } = await supabase.rpc(
      "colobongo_award_monthly_challenge_selection",
      {
        p_submission_id: String(submissionId),
        p_challenge_key: resolvedKey,
      }
    );
    if (selectionRewardError) {
      throw new Error(`Could not apply monthly challenge selection reward: ${selectionRewardError.message}`);
    }
    monthlyChallengeLevelXpGranted = Number(selectionRewardData) || 0;

    const { data: refreshedData, error: refreshedError } = await runWithSchemaFallback(
      () =>
        supabase
          .from(TABLE_NAME)
          .select(getSelectColumns())
          .eq("id", submissionId)
          .single(),
      "refresh_texture_monthly_challenge_status"
    );
    if (!refreshedError && refreshedData) {
      latestData = refreshedData;
    }
  }

  if (!latestData) return latestData;
  const hydrated = await hydrateSubmissionImage(latestData);
  return {
    ...hydrated,
    monthly_challenge_level_bonus_xp_granted: monthlyChallengeLevelXpGranted,
  };
}

export async function deleteTextureShowcaseSubmission(submissionId) {
  await ensureAdminAccess();

  // Fetch the submission to get image paths for storage cleanup
  const { data: submission, error: fetchError } = await supabase
    .from(TABLE_NAME)
    .select("id, image_url, thumbnail_storage_path")
    .eq("id", submissionId)
    .single();

  if (fetchError || !submission) {
    throw new Error(`Could not find submission to delete: ${fetchError?.message || "not found"}`);
  }

  // Delete the submission record from the database
  const { error: deleteError } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq("id", submissionId);

  if (deleteError) {
    throw new Error(`Could not delete submission: ${deleteError.message}`);
  }

  // Best-effort cleanup of the uploaded images from storage
  const pathsToRemove = [submission.image_url, submission.thumbnail_storage_path].filter(Boolean);
  if (pathsToRemove.length) {
    try {
      await supabase.storage
        .from(BUCKET_NAME)
        .remove(pathsToRemove);
    } catch (_storageError) {
      // Storage cleanup is best-effort; the DB record is already deleted
    }
  }

  return { id: submissionId, image_url: submission.image_url };
}

function sortSelectedRows(rows = []) {
  return rows
    .slice()
    .sort((a, b) => resolveSelectedOrderingDate(b) - resolveSelectedOrderingDate(a));
}

function resolveRowSelectedMonth(row) {
  return resolveSelectedMonthValue(
    row?.selected_month,
    row?.selected_at || row?.monthly_challenge_selected_at || row?.created_at
  );
}

function isSpotlightVisibilityApproved(row) {
  const hasWallStatus = row?.wall_status !== undefined && row?.wall_status !== null;
  const hasShowcaseStatus = row?.showcase_status !== undefined && row?.showcase_status !== null;
  if (!hasWallStatus && !hasShowcaseStatus) return true;

  const wallStatus = normalizeWallStatus(row?.wall_status);
  const showcaseStatus = normalizeWallStatus(row?.showcase_status);
  return wallStatus === "approved" || showcaseStatus === "approved";
}

function isSpotlightRowDisplayable(row) {
  if (!row) return false;
  if (!row?.image_url) return false;
  if (!row?.showcase_consent) return false;
  if (row?.removed_from_wall_at) return false;
  if (!row?.selected_at) return false;
  return isSpotlightVisibilityApproved(row);
}

function buildSpotlightQuery({ month = null, limit = 180 } = {}) {
  let query = supabase
    .from(TABLE_NAME)
    .select(getSpotlightSelectColumns())
    .in("selection_type", ["general", "contributor", "challenge"])
    .not("selected_at", "is", null)
    .order("selected_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .limit(limit);

  if (month && !ignoredSpotlightSchemaColumns.has("selected_month")) {
    query = query.eq("selected_month", month);
  }

  return query;
}

async function querySpotlightRows({ month = null, limit = 180, contextLabel = "query_spotlight_rows" } = {}) {
  const maxAttempts = 16;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await buildSpotlightQuery({ month, limit });
    if (!error) {
      return { data: Array.isArray(data) ? data : [], error: null, migrationMissing: false };
    }

    if (!isMissingColumnError(error)) {
      return { data: null, error, migrationMissing: false };
    }

    const missingColumn = normalizeMissingColumnName(extractMissingColumnName(error));
    if (!missingColumn) {
      return { data: null, error, migrationMissing: false };
    }

    if (SPOTLIGHT_REQUIRED_COLUMNS.has(missingColumn)) {
      return {
        data: [],
        error: new Error(`Spotlight required column missing: ${missingColumn}`),
        migrationMissing: true,
      };
    }

    const retried = registerMissingSpotlightSchemaColumn(error, contextLabel);
    if (!retried) {
      return { data: null, error, migrationMissing: false };
    }
  }

  return {
    data: null,
    error: new Error("Could not query spotlight rows after schema fallbacks."),
    migrationMissing: false,
  };
}

export async function fetchSpotlightDiagnostics({ month = null } = {}) {
  await ensureAdminAccess();
  const maxAttempts = 24;
  const contextLabel = "fetch_spotlight_diagnostics";

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const diagnosticOrFilters = [
      "selection_type.not.is.null",
      "selected_at.not.is.null",
      "selected_month.not.is.null",
    ];
    if (!ignoredSpotlightDiagnosticSchemaColumns.has("monthly_feature_status")) {
      diagnosticOrFilters.push("monthly_feature_status.eq.selected");
    }
    if (!ignoredSpotlightDiagnosticSchemaColumns.has("monthly_challenge_status")) {
      diagnosticOrFilters.push("monthly_challenge_status.eq.selected");
    }

    let query = supabase
      .from(TABLE_NAME)
      .select(getSpotlightDiagnosticSelectColumns())
      .or(diagnosticOrFilters.join(","))
      .order("selected_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(500);

    if (isValidYearMonth(month) && !ignoredSpotlightDiagnosticSchemaColumns.has("selected_month")) {
      query = query.eq("selected_month", month);
    }

    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await query;

    if (!error) {
      const rows = Array.isArray(data) ? data : [];
      const contributorIds = await resolveContributorUserIds(rows.map((row) => row?.user_id));
      return rows.map((row) => ({
        ...row,
        selection_type: row?.selection_type ? String(row.selection_type).trim().toLowerCase() : null,
        has_image: Boolean(row?.image_url),
        contributor_active:
          Boolean(row?.submitted_by_contributor) ||
          contributorIds.has(String(row?.user_id || "").trim()),
      }));
    }

    if (!registerMissingSpotlightDiagnosticSchemaColumn(error, contextLabel)) {
      throw new Error(`Could not fetch spotlight diagnostics: ${error.message}`);
    }
  }

  throw new Error("Could not fetch spotlight diagnostics after schema fallbacks.");
}

export async function fetchLatestCoupDeCoeur({ selectionType = "general" } = {}) {
  const normalizedSelectionType = normalizeSelectionType(selectionType);
  const selectedRowsResult = await querySpotlightRows({
    month: null,
    limit: 120,
    contextLabel: "fetch_latest_coup_de_coeur",
  });

  if (selectedRowsResult.error) {
    if (selectedRowsResult.migrationMissing) return null;
    throw new Error(`Could not fetch monthly pick: ${selectedRowsResult.error.message}`);
  }

  const selectedRows = Array.isArray(selectedRowsResult.data) ? selectedRowsResult.data : [];
  const candidates = selectedRows.filter((row) => {
    if (!isSpotlightRowDisplayable(row)) return false;
    return matchesSelectionType(row, normalizedSelectionType);
  });

  if (!candidates.length) return null;

  const sortedCandidates = sortSelectedRows(candidates);
  const latest = sortedCandidates[0];
  const hydrated = await hydrateSubmissionImage(latest);

  return {
    ...hydrated,
    selection_type: hydrated?.selection_type || normalizedSelectionType,
    selected_month: resolveSelectedMonthValue(
      hydrated?.selected_month,
      hydrated?.selected_at || hydrated?.monthly_challenge_selected_at || hydrated?.created_at
    ),
  };
}

function resolveRowsBySelectionType(rows = []) {
  const latestByType = {
    general: null,
    contributor: null,
    challenge: null,
  };
  const orderedTypes = ["general", "contributor", "challenge"];

  const sortedRows = sortSelectedRows(rows);
  sortedRows.forEach((row) => {
    const rawType = String(row?.selection_type || "").trim().toLowerCase();
    let type = normalizeSelectionType(rawType);
    if (!rawType && Boolean(row?.participates_in_monthly_challenge)) {
      type = "challenge";
    } else if (!rawType && Boolean(row?.submitted_by_contributor)) {
      type = "contributor";
    }
    if (!orderedTypes.includes(type)) return;
    if (!latestByType[type]) {
      latestByType[type] = row;
    }
  });

  return latestByType;
}

export async function getMonthlySpotlightSelections({ month = null } = {}) {
  const targetMonth = resolveSelectedMonthValue(month, getPreviousMonthKey());
  const debugEnabled = process.env.NODE_ENV !== "production";
  if (debugEnabled) {
    // eslint-disable-next-line no-console
    console.info("[SPOTLIGHT] targetMonth", { targetMonth });
  }

  const allQuery = await querySpotlightRows({
    month: null,
    limit: 240,
    contextLabel: "get_monthly_spotlight_all_rows",
  });

  if (allQuery.error) {
    if (allQuery.migrationMissing) {
      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.info("[SPOTLIGHT] error", {
          stage: "all-rows",
          month: targetMonth,
          migrationMissing: true,
          message: allQuery.error?.message || null,
        });
      }
      return {
        month: targetMonth,
        items: [],
        source: "migration-missing",
        migrationMissing: true,
      };
    }

    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.info("[SPOTLIGHT] error", {
        stage: "all-rows",
        month: targetMonth,
        message: allQuery.error?.message || null,
      });
    }
    throw new Error(`Could not fetch monthly spotlight selections: ${allQuery.error.message}`);
  }

  const orderedTypes = ["general", "contributor", "challenge"];
  const allRows = (Array.isArray(allQuery.data) ? allQuery.data : [])
    .map((row) => ({
      ...row,
      selection_type: row?.selection_type ? String(row.selection_type).trim().toLowerCase() : null,
    }));

  const targetMonthRows = allRows.filter((row) => {
    const rowMonth = resolveRowSelectedMonth(row);
    return rowMonth === targetMonth;
  });

  let resolvedMonth = targetMonth;
  let source = "target-month";
  if (!targetMonthRows.length) {
    const latestSelectedRow = sortSelectedRows(
      allRows.filter((row) => Boolean(row?.selected_at))
    )[0];
    const latestMonth = resolveRowSelectedMonth(latestSelectedRow);
    if (latestMonth) {
      resolvedMonth = latestMonth;
      source = "latest-available";
    }
  }

  const rawRowsForResolvedMonth = allRows.filter((row) => {
    const rowMonth = resolveRowSelectedMonth(row);
    return rowMonth === resolvedMonth;
  });

  const acceptedRows = [];
  const rejectedRows = [];

  rawRowsForResolvedMonth.forEach((row) => {
    const reasons = [];
    const selectionType = String(row?.selection_type || "").trim().toLowerCase();
    if (!selectionType) reasons.push("missing_selection_type");
    if (selectionType && !orderedTypes.includes(selectionType)) reasons.push("missing_selection_type");
    if (!row?.selected_month) reasons.push("missing_selected_month");
    if (!row?.selected_at) reasons.push("missing_selected_at");
    if (!row?.image_url) reasons.push("missing_image");
    if (!Boolean(row?.showcase_consent)) reasons.push("missing_consent");
    if (row?.removed_from_wall_at) reasons.push("removed_from_wall");
    if (!isSpotlightVisibilityApproved(row)) reasons.push("not_approved");

    const rowMonth = resolveRowSelectedMonth(row);
    if (resolvedMonth && rowMonth !== resolvedMonth) reasons.push("month_mismatch");

    if (reasons.length) {
      rejectedRows.push({
        id: row?.id || null,
        name: row?.showcase_name || null,
        selection_type: selectionType || null,
        selected_month: row?.selected_month || null,
        selected_at: row?.selected_at || null,
        reasons,
      });
      return;
    }

    acceptedRows.push({
      ...row,
      selection_type: selectionType,
    });
  });

  const byType = resolveRowsBySelectionType(acceptedRows);
  const entries = await Promise.all(
    Object.entries(byType)
      .filter(([, row]) => Boolean(row))
      .map(async ([type, row]) => {
        try {
          const hydrated = await hydrateSubmissionImage(row);
          return {
            ...hydrated,
            selection_type: type,
            selected_month: resolveRowSelectedMonth(hydrated),
          };
        } catch (_imageError) {
          return {
            ...row,
            selection_type: type,
            selected_month: resolveRowSelectedMonth(row),
            image_display_url: row?.image_url || null,
            thumbnail_display_url: row?.image_url || null,
            full_display_url: row?.image_url || null,
          };
        }
      })
  );

  if (debugEnabled) {
    // eslint-disable-next-line no-console
    console.info("[SPOTLIGHT_DIAGNOSTIC] raw rows", rawRowsForResolvedMonth.map((row) => ({
      id: row?.id || null,
      name: row?.showcase_name || null,
      type: row?.selection_type || null,
      month: row?.selected_month || null,
      selected_at: row?.selected_at || null,
      wall_status: row?.wall_status || null,
      showcase_status: row?.showcase_status || null,
      showcase_consent: row?.showcase_consent ?? null,
      has_image: Boolean(row?.image_url),
      removed_from_wall_at: row?.removed_from_wall_at || null,
    })));
    // eslint-disable-next-line no-console
    console.info("[SPOTLIGHT_DIAGNOSTIC] accepted rows", acceptedRows.map((row) => ({
      id: row?.id || null,
      name: row?.showcase_name || null,
      type: row?.selection_type || null,
      month: row?.selected_month || null,
      selected_at: row?.selected_at || null,
    })));
    // eslint-disable-next-line no-console
    console.info("[SPOTLIGHT_DIAGNOSTIC] rejected rows", rejectedRows);
    // eslint-disable-next-line no-console
    console.info("[SPOTLIGHT] grouped types", {
      groupedTypes: entries.map((entry) => entry?.selection_type).filter(Boolean),
    });
    // eslint-disable-next-line no-console
    console.info("[SPOTLIGHT] resolvedMonth", {
      targetMonth,
      resolvedMonth,
      source,
      fetchedRows: rawRowsForResolvedMonth.length,
      acceptedRows: acceptedRows.length,
      rejectedRows: rejectedRows.length,
    });
  }

  return {
    month: resolvedMonth,
    items: entries,
    source,
    migrationMissing: Boolean(allQuery.migrationMissing),
  };
}

export async function selectMonthlySpotlight({
  submissionId,
  selectionType = "general",
  selectedMonth = null,
  challengeTheme = "",
  spotlightNote = "",
} = {}) {
  if (!submissionId) {
    throw new Error("submissionId is required.");
  }

  const normalizedType = normalizeSelectionType(selectionType);
  const resolvedSelectedMonth = resolveSelectedMonthValue(selectedMonth, getPreviousMonthKey());
  const normalizedChallengeTheme = String(challengeTheme || "").trim() || null;
  const normalizedSpotlightNote = String(spotlightNote || "").trim() || null;
  const debugEnabled = process.env.NODE_ENV !== "production";

  try {
    await ensureAdminAccess();

    if (normalizedType === "contributor") {
      const { data: submissionRow, error: submissionError } = await runWithSchemaFallback(
        () =>
          supabase
            .from(TABLE_NAME)
            .select("id, user_id, showcase_name, artwork_title, submitted_by_contributor")
            .eq("id", submissionId)
            .single(),
        "spotlight_contributor_eligibility_check"
      );

      if (submissionError) {
        throw new Error(`Could not verify contributor eligibility: ${submissionError.message}`);
      }

      const contributorActive = await resolveSubmissionContributorFlag(
        submissionRow?.user_id,
        Boolean(submissionRow?.submitted_by_contributor)
      );

      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.info("[SPOTLIGHT_CONTRIBUTOR_ELIGIBILITY]", {
          submissionId,
          showcaseName: submissionRow?.showcase_name || null,
          artworkTitle: submissionRow?.artwork_title || null,
          userId: submissionRow?.user_id || null,
          submittedByContributor: Boolean(submissionRow?.submitted_by_contributor),
          contributorActive,
        });
      }

      if (!contributorActive) {
        throw new Error("Cette publication n est pas liee a un Contributeur Bongo actif.");
      }
    }

    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.info("[SPOTLIGHT_SELECT_REQUEST]", {
        submissionId,
        selectionType,
        normalizedType,
        selectedMonth,
        resolvedSelectedMonth,
        challengeTheme: normalizedChallengeTheme,
      });
    }
    const { data: rpcData, error: rpcError } = await supabase.rpc("admin_select_monthly_spotlight", {
      p_submission_id: submissionId,
      p_selection_type: normalizedType,
      p_selected_month: resolvedSelectedMonth,
      p_challenge_theme: normalizedChallengeTheme,
      p_spotlight_note: normalizedSpotlightNote,
    });

    if (rpcError) {
      throw rpcError;
    }

    const normalizedRpcPayload = Array.isArray(rpcData)
      ? (rpcData[0] || {})
      : (rpcData || {});

    const { data: refreshedData, error: refreshedError } = await runWithSchemaFallback(
      () =>
        supabase
          .from(TABLE_NAME)
          .select(getSelectColumns())
          .eq("id", submissionId)
          .single(),
      "refresh_after_admin_select_monthly_spotlight"
    );

    if (refreshedError) {
      throw new Error(`Could not refresh selected submission: ${refreshedError.message}`);
    }

    const hydrated = await hydrateSubmissionImage(refreshedData);
    const contributorActive = await resolveSubmissionContributorFlag(
      hydrated?.user_id,
      Boolean(hydrated?.submitted_by_contributor)
    );
    const verifiedSelectionType = String(hydrated?.selection_type || "").trim().toLowerCase();
    const verifiedSelectedMonth = String(hydrated?.selected_month || "").trim();
    const verifiedSelectedAt = hydrated?.selected_at || null;

    if (
      verifiedSelectionType !== normalizedType ||
      verifiedSelectedMonth !== resolvedSelectedMonth ||
      !verifiedSelectedAt
    ) {
      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.info("[SPOTLIGHT_SELECT_RESPONSE]", {
          submissionId,
          expected: {
            selection_type: normalizedType,
            selected_month: resolvedSelectedMonth,
          },
          actual: {
            selection_type: verifiedSelectionType || null,
            selected_month: verifiedSelectedMonth || null,
            selected_at: verifiedSelectedAt,
          },
          rpcPayload: normalizedRpcPayload,
        });
      }
      if (!verifiedSelectedAt) {
        throw new Error("Spotlight verification failed: selected row was not updated.");
      }
      throw new Error(
        `Spotlight verification failed: requested ${normalizedType} but database saved ${verifiedSelectionType || "null"} (${verifiedSelectedMonth || "null"}).`
      );
    }

    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.info("[SPOTLIGHT_SELECT_RESPONSE]", {
        submissionId,
        selection_type: verifiedSelectionType,
        selected_month: verifiedSelectedMonth,
        selected_at: verifiedSelectedAt,
        reward_already_granted: Boolean(normalizedRpcPayload?.reward_already_granted),
      });
    }

    return {
      ...hydrated,
      contributor_active:
        Boolean(hydrated?.submitted_by_contributor) || contributorActive,
      reward_already_granted: Boolean(normalizedRpcPayload?.reward_already_granted),
      level_reward_granted: Boolean(normalizedRpcPayload?.level_reward_granted),
      contributor_reward_granted: Boolean(normalizedRpcPayload?.contributor_reward_granted),
      contributor_reward_ends_at: normalizedRpcPayload?.contributor_reward_ends_at || null,
    };
  } catch (error) {
    if (!isRpcMissingError(error, "admin_select_monthly_spotlight")) {
      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.info("[SPOTLIGHT_SELECT_ERROR]", {
          submissionId,
          selectionType,
          selectedMonth: resolvedSelectedMonth,
          message: error?.message || null,
          code: error?.code || null,
          details: error?.details || null,
          hint: error?.hint || null,
        });
      }
      throw error;
    }

    throw new Error(
      "La fonction Supabase des Coups de Coeur n est pas encore disponible. Applique la migration SQL, puis réessaie."
    );
  }
}

export async function clearMonthlySpotlightSelection({
  submissionId,
  monthlyChallengeKey = MONTHLY_CHALLENGE_KEY,
} = {}) {
  if (!submissionId) {
    throw new Error("submissionId is required.");
  }

  try {
    await ensureAdminAccess();
    const { error: rpcError } = await supabase.rpc("admin_clear_monthly_spotlight", {
      p_submission_id: submissionId,
    });
    if (rpcError) {
      throw rpcError;
    }

    const { data: refreshedData, error: refreshedError } = await runWithSchemaFallback(
      () =>
        supabase
          .from(TABLE_NAME)
          .select(getSelectColumns())
          .eq("id", submissionId)
          .single(),
      "refresh_after_admin_clear_monthly_spotlight"
    );

    if (refreshedError) {
      throw new Error(`Could not refresh cleared submission: ${refreshedError.message}`);
    }

    return hydrateSubmissionImage(refreshedData);
  } catch (error) {
    if (!isRpcMissingError(error, "admin_clear_monthly_spotlight")) {
      throw error;
    }

    // Fallback compatibility path when the RPC migration is not deployed yet.
    return updateTextureMonthlyChallengeStatus(
      submissionId,
      "none",
      "general",
      monthlyChallengeKey,
      { clearSpotlight: true }
    );
  }
}

export async function updateMySpotlightTikTok({
  submissionId,
  tiktokUsername = "",
  language = "fr",
} = {}) {
  const normalizedSubmissionId = String(submissionId || "").trim();
  if (!normalizedSubmissionId) {
    throw new Error("submissionId is required.");
  }

  await ensureAuthenticatedSessionUser();

  let normalizedUsername = "";
  try {
    normalizedUsername = validateTikTokUsernameOrThrow(tiktokUsername, language);
  } catch (validationError) {
    throw new Error(validationError?.message || "Invalid TikTok username.");
  }

  const { data: rpcData, error: rpcError } = await supabase.rpc("update_my_spotlight_tiktok", {
    p_submission_id: normalizedSubmissionId,
    p_tiktok_username: normalizedUsername || null,
  });

  if (rpcError) {
    if (isRpcMissingError(rpcError, "update_my_spotlight_tiktok")) {
      throw new Error(
        language === "fr"
          ? "Le champ TikTok spotlight n est pas encore pret dans Supabase. Applique la migration SQL, puis réessaie."
          : "The spotlight TikTok field is not ready in Supabase yet. Apply the SQL migration, then try again."
      );
    }
    throw new Error(
      language === "fr"
        ? "Impossible de mettre a jour ton TikTok pour le moment."
        : "Could not update your TikTok right now."
    );
  }

  const payload = Array.isArray(rpcData) ? (rpcData[0] || {}) : (rpcData || {});

  const { data: refreshedData, error: refreshedError } = await runWithSchemaFallback(
    () =>
      supabase
        .from(TABLE_NAME)
        .select(getSelectColumns())
        .eq("id", normalizedSubmissionId)
        .single(),
    "refresh_after_update_my_spotlight_tiktok"
  );

  if (refreshedError) {
    throw new Error(`Could not refresh spotlight TikTok submission: ${refreshedError.message}`);
  }

  const hydrated = await hydrateSubmissionImage(refreshedData);
  return {
    ...hydrated,
    spotlight_tiktok_username:
      hydrated?.spotlight_tiktok_username ?? payload?.spotlight_tiktok_username ?? null,
    spotlight_tiktok_updated_at:
      hydrated?.spotlight_tiktok_updated_at ?? payload?.spotlight_tiktok_updated_at ?? null,
  };
}

export async function getSelectedColoringOfTheMonth() {
  return fetchLatestCoupDeCoeur({ selectionType: "general" });
}

async function queryPublicWallRowsBatch({ from = 0, to = 59, previewMode = false } = {}) {
  const contextLabel = previewMode ? "query_public_wall_rows_batch_preview" : "query_public_wall_rows_batch";
  const maxAttempts = 24;
  let includeWallStatusFilter = !ignoredSchemaColumns.has("wall_status");
  let includeShowcaseStatusFilter = !ignoredSchemaColumns.has("showcase_status");
  let includeWallApprovedOrder = !ignoredSchemaColumns.has("wall_approved_at");

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    let query = supabase
      .from(TABLE_NAME)
      .select(previewMode ? getPublicWallPreviewSelectColumns() : getPublicWallSelectColumns())
      .eq("showcase_consent", true);

    if (includeWallStatusFilter && includeShowcaseStatusFilter) {
      query = query.or("wall_status.eq.approved,showcase_status.eq.approved");
    } else if (includeWallStatusFilter) {
      query = query.eq("wall_status", "approved");
    } else if (includeShowcaseStatusFilter) {
      query = query.eq("showcase_status", "approved");
    }

    if (includeWallApprovedOrder) {
      query = query.order("wall_approved_at", { ascending: false });
    }

    query = query
      .order("created_at", { ascending: false })
      .range(Math.max(0, Number(from) || 0), Math.max(0, Number(to) || 0));

    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await query;
    if (!error) {
      return { data, error: null };
    }

    const schemaRetryHandled = registerMissingSchemaColumn(error, contextLabel);
    if (schemaRetryHandled) {
      continue;
    }

    if (!isMissingColumnError(error)) {
      return { data: null, error };
    }

    const missingColumn = extractMissingColumnName(error);
    if (missingColumn) {
      registerMissingColumnByName(missingColumn, contextLabel);
      if (missingColumn === "wall_status") includeWallStatusFilter = false;
      if (missingColumn === "showcase_status") includeShowcaseStatusFilter = false;
      if (missingColumn === "wall_approved_at") includeWallApprovedOrder = false;
      continue;
    }

    return { data: null, error };
  }

  const fallbackContext = previewMode
    ? "query_public_wall_rows_batch_fallback_preview"
    : "query_public_wall_rows_batch_fallback";
  const fallbackResult = await runWithSchemaFallback(
    () =>
      supabase
        .from(TABLE_NAME)
        .select(CORE_SELECT_COLUMNS.join(",\n  "))
        .eq("showcase_consent", true)
        .eq("showcase_status", "approved")
        .order("created_at", { ascending: false })
        .range(Math.max(0, Number(from) || 0), Math.max(0, Number(to) || 0)),
    fallbackContext
  );

  return fallbackResult;
}

export async function fetchApprovedShowcaseSubmissions({
  limit = 100,
  page = 0,
  section = "all",
  monthlyChallengeKey = MONTHLY_CHALLENGE_KEY,
  preview = false,
} = {}) {
  const previewMode = Boolean(preview);
  if (!previewMode) {
    await markExpiredWallEntriesBestEffort();
  }

  const safeLimit = Number.isFinite(Number(limit))
    ? Math.min(Math.max(Number(limit), 1), previewMode ? 12 : 100)
    : (previewMode ? 8 : 24);
  const safePage = Number.isFinite(Number(page)) ? Math.max(0, Math.floor(Number(page))) : 0;
  const targetStart = safePage * safeLimit;
  const targetEndExclusive = targetStart + safeLimit;
  const batchSize = previewMode
    ? Math.max(safeLimit * 2, 24)
    : Math.max(safeLimit * 3, 72);
  const maxScanBatches = previewMode ? 4 : 50;

  const visibleRowsAccumulator = [];
  const seenIds = new Set();
  let scanOffset = 0;
  let hasMoreRawRows = true;

  for (let batchIndex = 0; batchIndex < maxScanBatches; batchIndex += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await queryPublicWallRowsBatch({
      from: scanOffset,
      to: scanOffset + batchSize - 1,
      previewMode,
    });

    if (error) {
      throw new Error(`Could not fetch wall entries: ${error.message}`);
    }

    const batchRows = Array.isArray(data) ? data : [];
    if (!batchRows.length) {
      hasMoreRawRows = false;
      break;
    }

    const filteredBatchRows = filterPublicWallEntries(batchRows, {
      section,
      monthlyChallengeKey,
    });

    filteredBatchRows.forEach((row) => {
      const rowId = String(row?.id || "").trim();
      if (!rowId || seenIds.has(rowId)) return;
      seenIds.add(rowId);
      visibleRowsAccumulator.push(row);
    });

    if (visibleRowsAccumulator.length >= targetEndExclusive) {
      if (batchRows.length < batchSize) {
        hasMoreRawRows = false;
      }
      break;
    }

    if (batchRows.length < batchSize) {
      hasMoreRawRows = false;
      break;
    }

    scanOffset += batchSize;
  }

  const pageRows = visibleRowsAccumulator.slice(targetStart, targetEndExclusive);
  const hasMore =
    visibleRowsAccumulator.length > targetEndExclusive ||
    hasMoreRawRows;

  const hydratedRows = await Promise.all(
    pageRows.map((row) =>
      hydrateSubmissionImage(row, previewMode
        ? {
            preview: true,
            thumbnailOptions: {
              width: 520,
              quality: 72,
              resize: "cover",
              format: "webp",
            },
          }
        : undefined)
    )
  );

  if (previewMode) {
    return {
      items: hydratedRows.map((row) => pickPublicWallPreviewFields(row)),
      hasMore,
      page: safePage,
      limit: safeLimit,
    };
  }

  const submissionIds = hydratedRows.map((row) => row?.id).filter(Boolean);

  if (!submissionIds.length) {
    return {
      items: hydratedRows,
      hasMore,
      page: safePage,
      limit: safeLimit,
    };
  }

  let currentUserId = null;
  try {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (!authError) {
      currentUserId = authData?.user?.id || null;
    }
  } catch (_error) {
    currentUserId = null;
  }

  let reactionSnapshot = null;
  try {
    reactionSnapshot = await fetchWallReactionSnapshot({
      submissionIds,
      currentUserId,
    });
  } catch (_error) {
    reactionSnapshot = null;
  }

  const userIdsForResolvers = hydratedRows.map((row) => row?.user_id);
  // CODEX_PROMPT_74 : resolveur frigo_is_public en parallele de contributor_form
  // pour eviter une round-trip supplementaire.
  const [contributorForms, frigoPublicSet] = await Promise.all([
    resolveContributorForms(userIdsForResolvers),
    resolveFrigoPublicUserIds(userIdsForResolvers),
  ]);

  const items = hydratedRows.map((row) => {
    const typeCounts = reactionSnapshot?.countsByTypeBySubmissionId?.[row.id] || {};
    const reactedTypes = reactionSnapshot?.reactedTypesBySubmissionId?.[row.id] || new Set();
    const normalizedUserId = String(row?.user_id || "").trim();
    return {
      ...row,
      submitted_by_contributor:
        Boolean(row?.submitted_by_contributor) ||
        contributorForms.has(normalizedUserId),
      contributor_form: contributorForms.get(normalizedUserId) || null,
      // CODEX_PROMPT_74 : flag pour le WallFrigoLinkIndicator.
      frigo_is_public: frigoPublicSet.has(normalizedUserId),
      reaction_count: reactionSnapshot?.countsBySubmissionId?.[row.id] || 0,
      reacted_by_me: Boolean(reactionSnapshot?.reactedSubmissionIds?.has(row.id)),
      reaction_counts_by_type: {
        [REACTION_TYPE_BONGO_HEART]: Number(typeCounts[REACTION_TYPE_BONGO_HEART]) || 0,
        [REACTION_TYPE_GORGEOUS]: Number(typeCounts[REACTION_TYPE_GORGEOUS]) || 0,
        [REACTION_TYPE_INSPIRING]: Number(typeCounts[REACTION_TYPE_INSPIRING]) || 0,
      },
      reacted_types: Array.from(reactedTypes),
    };
  });

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.info("[WALL_DEBUG] page fetch summary", {
      section,
      page: safePage,
      limit: safeLimit,
      fetchedInPage: items.length,
      hasMore,
      names: items.map((entry) => entry?.showcase_name || null).filter(Boolean),
    });
  }

  return {
    items,
    hasMore,
    page: safePage,
    limit: safeLimit,
  };
}

export async function fetchPublicWallEntries({
  limit = 24,
  page = 0,
  section = "all",
  monthlyChallengeKey = MONTHLY_CHALLENGE_KEY,
  preview = false,
} = {}) {
  const result = await fetchApprovedShowcaseSubmissions({
    limit,
    page,
    section,
    monthlyChallengeKey,
    preview,
  });

  if (preview) {
    return result.items;
  }

  return result.items;
}

export async function fetchWallReactionSnapshot({ submissionIds = [], currentUserId = null } = {}) {
  const ids = Array.from(
    new Set(
      (Array.isArray(submissionIds) ? submissionIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (!ids.length) {
    return {
      countsBySubmissionId: {},
      reactedSubmissionIds: new Set(),
    };
  }

  let countRows = null;
  let countError = null;
  ({ data: countRows, error: countError } = await supabase
    .from(REACTION_COUNT_VIEW_NAME)
    .select("submission_id, reaction_count, cute_count, gorgeous_count, inspiring_count")
    .in("submission_id", ids));

  if (countError && /column .* does not exist/i.test(countError.message || "")) {
    // Fallback for environments where the per-type view migration hasn't been applied yet.
    const fallback = await supabase
      .from(REACTION_COUNT_VIEW_NAME)
      .select("submission_id, reaction_count")
      .in("submission_id", ids);
    countRows = (fallback.data || []).map((row) => ({
      ...row,
      cute_count: row.reaction_count,
      gorgeous_count: 0,
      inspiring_count: 0,
    }));
    countError = fallback.error;
  }

  if (countError) {
    throw new Error(`Could not fetch wall reaction counts: ${countError.message}`);
  }

  const countsBySubmissionId = (countRows || []).reduce((acc, row) => {
    const key = String(row?.submission_id || "").trim();
    if (!key) return acc;
    acc[key] = Number(row?.reaction_count) || 0;
    return acc;
  }, {});

  const countsByTypeBySubmissionId = (countRows || []).reduce((acc, row) => {
    const key = String(row?.submission_id || "").trim();
    if (!key) return acc;
    acc[key] = {
      [REACTION_TYPE_BONGO_HEART]: Number(row?.cute_count) || 0,
      [REACTION_TYPE_GORGEOUS]: Number(row?.gorgeous_count) || 0,
      [REACTION_TYPE_INSPIRING]: Number(row?.inspiring_count) || 0,
    };
    return acc;
  }, {});

  try {
    const { data: reactionRows, error: reactionRowsError } = await supabase
      .from(REACTION_TABLE_NAME)
      .select("submission_id, user_id")
      .in("submission_id", ids)
      .eq("reaction_type", REACTION_TYPE_BONGO_HEART);

    if (!reactionRowsError && Array.isArray(reactionRows) && reactionRows.length) {
      const adminUserIds = await resolveAdminReactionUserIds(
        reactionRows.map((row) => row?.user_id)
      );

      if (adminUserIds.size) {
        const weightedCounts = applyAdminReactionWeight(
          countsBySubmissionId,
          reactionRows,
          adminUserIds
        );

        Object.keys(countsBySubmissionId).forEach((key) => {
          countsBySubmissionId[key] = Number(weightedCounts[key] || 0);
        });
      }
    }
  } catch (_error) {
    // Best effort only; fallback stays on the materialized count view values.
  }

  const reactedSubmissionIds = new Set();
  const reactedTypesBySubmissionId = {};
  if (currentUserId) {
    const { data: reactedRows, error: reactedError } = await supabase
      .from(REACTION_TABLE_NAME)
      .select("submission_id, reaction_type")
      .eq("user_id", currentUserId)
      .in("submission_id", ids);

    if (reactedError) {
      throw new Error(`Could not fetch your Bongo reactions: ${reactedError.message}`);
    }

    (reactedRows || []).forEach((row) => {
      const key = String(row?.submission_id || "").trim();
      if (!key) return;
      reactedSubmissionIds.add(key);
      const type = String(row?.reaction_type || REACTION_TYPE_BONGO_HEART).trim();
      if (!reactedTypesBySubmissionId[key]) reactedTypesBySubmissionId[key] = new Set();
      reactedTypesBySubmissionId[key].add(type);
    });
  }

  return {
    countsBySubmissionId,
    countsByTypeBySubmissionId,
    reactedSubmissionIds,
    reactedTypesBySubmissionId,
  };
}

export async function toggleWallBongoReaction({ submissionId, userId, reactionType = REACTION_TYPE_BONGO_HEART }) {
  const normalizedSubmissionId = String(submissionId || "").trim();
  if (!normalizedSubmissionId) {
    throw new Error("submissionId is required.");
  }
  if (!userId) {
    throw new Error("AUTH_REQUIRED: Sign in to send a Bongo reaction.");
  }

  const normalizedType = WALL_REACTION_TYPES.includes(reactionType)
    ? reactionType
    : REACTION_TYPE_BONGO_HEART;

  await ensureAuthenticatedSessionUser(userId);

  const { data: existingReaction, error: existingError } = await supabase
    .from(REACTION_TABLE_NAME)
    .select("id")
    .eq("submission_id", normalizedSubmissionId)
    .eq("user_id", userId)
    .eq("reaction_type", normalizedType)
    .maybeSingle();

  if (existingError && existingError.code !== 'PGRST116') {
    throw new Error(`Could not check existing reaction: ${existingError.message}`);
  }

  const alreadyReacted = Boolean(existingReaction);
  let reacted;

  if (alreadyReacted) {
    const { error: deleteError } = await supabase
      .from(REACTION_TABLE_NAME)
      .delete()
      .eq("submission_id", normalizedSubmissionId)
      .eq("user_id", userId)
      .eq("reaction_type", normalizedType);

    if (deleteError) {
      throw new Error(`Could not remove reaction: ${deleteError.message}`);
    }
    reacted = false;
  } else {
    const { error: insertError } = await supabase
      .from(REACTION_TABLE_NAME)
      .insert({ submission_id: normalizedSubmissionId, user_id: userId, reaction_type: normalizedType });

    if (insertError) {
      throw new Error(`Could not add reaction: ${insertError.message}`);
    }
    reacted = true;
  }

  let reactionCount = 0;
  let countsByType = {
    [REACTION_TYPE_BONGO_HEART]: 0,
    [REACTION_TYPE_GORGEOUS]: 0,
    [REACTION_TYPE_INSPIRING]: 0,
  };
  let reactedTypes = new Set();
  try {
    const snapshot = await fetchWallReactionSnapshot({
      submissionIds: [normalizedSubmissionId],
      currentUserId: userId,
    });
    reactionCount = Number(snapshot?.countsBySubmissionId?.[normalizedSubmissionId] || 0);
    countsByType = snapshot?.countsByTypeBySubmissionId?.[normalizedSubmissionId] || countsByType;
    reactedTypes = snapshot?.reactedTypesBySubmissionId?.[normalizedSubmissionId] || new Set();
  } catch (_error) {
    // Non-fatal: keep best-effort local count.
  }

  return {
    reacted,
    reactionType: normalizedType,
    reactionCount,
    countsByType,
    reactedTypes,
  };
}
